import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MicOffOutlinedIcon from '@mui/icons-material/MicOffOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ScreenShareOutlinedIcon from '@mui/icons-material/ScreenShareOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MicNoneIcon from '@mui/icons-material/MicNone';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import WifiIcon from '@mui/icons-material/Wifi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { resolveResolvedTheme, WidgetBuilderConfig } from '../../types/widgetBuilder';

const faPaperPlaneTop: IconDefinition = {
  prefix: 'fas',
  iconName: 'paper-plane',
  icon: [
    576,
    512,
    [],
    'f1d8',
    'M536.4-26.3c9.8-3.5 20.6-1 28 6.3s9.8 18.2 6.3 28l-178 496.9c-5 13.9-18.1 23.1-32.8 23.1-14.2 0-27-8.6-32.3-21.7l-64.2-158c-4.5-11-2.5-23.6 5.2-32.6l94.5-112.4c5.1-6.1 4.7-15-.9-20.6s-14.6-6-20.6-.9L229.2 276.1c-9.1 7.6-21.6 9.6-32.6 5.2L38.1 216.8c-13.1-5.3-21.7-18.1-21.7-32.3 0-14.7 9.2-27.8 23.1-32.8l496.9-178z',
  ],
};

type Props = {
  config: WidgetBuilderConfig;
  device: 'desktop' | 'mobile';
};

type ConversationItem = {
  id: string;
  name: string;
  preview: string;
  time: string;
  unread?: number;
  isGroup?: boolean;
  members?: number;
  online?: boolean;
  avatarColor: string;
};

type ThemeTokens = {
  isDark: boolean;
  brand: string;
  primary: string;
  secondary: string;
  surface: string;
  panel: string;
  chatBg: string;
  border: string;
  muted: string;
  softBrand: string;
  fontSize: number;
  fontFamily: string;
};

const hideScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
} as const;

const withAlpha = (hex: string, alphaHex: string) => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return `${hex}${alphaHex}`;
  return `#${normalized}${alphaHex}`;
};

const headerIconSx = (tokens: ThemeTokens) => ({
  width: 34,
  height: 34,
  borderRadius: '10px',
  color: tokens.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  '&:hover': {
    color: tokens.brand,
    backgroundColor: withAlpha(tokens.brand, '16'),
  },
});

const composerSideIconSx = (tokens: ThemeTokens) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: `1.5px solid ${tokens.border}`,
  backgroundColor: tokens.surface,
  color: tokens.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  '&:hover': {
    color: tokens.brand,
    borderColor: withAlpha(tokens.brand, '55'),
    backgroundColor: withAlpha(tokens.brand, '12'),
  },
});

const buildTokens = (config: WidgetBuilderConfig, systemDark = false): ThemeTokens => {
  const mode = config.theme === 'system' ? (systemDark ? 'dark' : 'light') : resolveResolvedTheme(config.theme);
  const isDark = mode === 'dark';
  const brand = config.colors.brand_color || '#007AFF';
  const primary =
    (isDark ? config.colors.primary_text_dark : config.colors.primary_text_light) ||
    (isDark ? '#FFFFFF' : '#0F172A');
  const secondary =
    (isDark ? config.colors.secondary_text_dark : config.colors.secondary_text_light) ||
    (isDark ? '#989898' : '#6B7280');

  return {
    isDark,
    brand,
    primary,
    secondary,
    surface: isDark ? '#0B1220' : '#FFFFFF',
    panel: isDark ? '#111827' : withAlpha(brand, '08'),
    chatBg: isDark ? '#0A1220' : withAlpha(brand, '06'),
    border: isDark ? 'rgba(148,163,184,0.18)' : '#E5E7EB',
    muted: secondary,
    softBrand: withAlpha(brand, isDark ? '28' : '12'),
    fontSize:
      config.typography.size === 'compact' ? 12 : config.typography.size === 'comfortable' ? 14.5 : 13,
    fontFamily: `"${config.typography.font || 'Poppins'}", sans-serif`,
  };
};

type KitScreenId =
  | 'inbox'
  | 'peer'
  | 'group'
  | 'peer-detail'
  | 'group-detail'
  | 'people'
  | 'calls'
  | 'groups'
  | 'scheduled'
  | 'schedule-form'
  | 'modals';

type KitModalId =
  | 'profile'
  | 'status'
  | 'create-group'
  | 'password'
  | 'group-options'
  | 'confirm'
  | 'info'
  | 'schedule'
  | 'reschedule'
  | 'incoming'
  | 'outgoing'
  | 'in-call'
  | 'reactions'
  | 'message-info'
  | 'delete-message'
  | 'delete-conversation'
  | 'unban'
  | 'ownership'
  | 'meeting-admin'
  | 'input'
  | 'attach'
  | 'search'
  | 'thread';

type ModalItem = { id: KitModalId; label: string; hint: string };

const anyCall = (config: WidgetBuilderConfig) =>
  config.call_feature.is_one_to_one_voice_calls ||
  config.call_feature.is_one_to_one_video_calls ||
  config.call_feature.is_group_voice_calls ||
  config.call_feature.is_group_video_calls;

const showVoiceCalls = (config: WidgetBuilderConfig) =>
  config.call_feature.is_one_to_one_voice_calls || config.call_feature.is_group_voice_calls;

const showVideoCalls = (config: WidgetBuilderConfig) =>
  config.call_feature.is_one_to_one_video_calls || config.call_feature.is_group_video_calls;

const showCallsNav = (config: WidgetBuilderConfig) => config.tabs.call_history_tab;

const isKitScreenVisible = (id: KitScreenId, config: WidgetBuilderConfig) => {
  if (id === 'inbox' || id === 'modals') return true;
  if (id === 'peer' || id === 'peer-detail' || id === 'people') return config.tabs.users_tab;
  if (id === 'group' || id === 'group-detail' || id === 'groups') return config.tabs.group_tab;
  if (id === 'calls') return showCallsNav(config);
  if (id === 'scheduled') return config.tabs.schedule_call_history_tab;
  if (id === 'schedule-form') return config.call_feature.is_schedule_calls;
  return true;
};

const isKitModalVisible = (id: KitModalId, config: WidgetBuilderConfig) => {
  const chat = config.chat_feature;
  const call = config.call_feature;
  if (id === 'password') return chat.is_private_and_protected_group;
  if (id === 'create-group') return chat.is_public_group || chat.is_private_and_protected_group;
  if (id === 'unban' || id === 'meeting-admin') return chat.is_ban_user || call.is_group_voice_calls || call.is_group_video_calls;
  if (id === 'reactions') return chat.is_message_reaction;
  if (id === 'message-info') return chat.is_read_and_delivered_receipts;
  if (id === 'delete-message') return chat.is_delete_chat;
  if (id === 'delete-conversation') return chat.is_delete_conversation;
  if (id === 'thread') return chat.is_thread;
  if (id === 'search') return chat.is_search_message;
  if (id === 'attach') return chat.is_media_file_sharing;
  if (id === 'schedule' || id === 'reschedule') return call.is_schedule_calls;
  if (id === 'incoming' || id === 'outgoing' || id === 'in-call') return anyCall(config);
  if (id === 'confirm') return chat.is_block_user || chat.is_delete_conversation;
  return true;
};

const modalItems = (config: WidgetBuilderConfig): ModalItem[] =>
  (
    [
      { id: 'profile', label: 'Profile', hint: 'Your status and presence' },
      { id: 'status', label: 'Custom status', hint: 'Set a status message' },
      { id: 'create-group', label: 'Create group', hint: 'Public, private, or protected' },
      { id: 'password', label: 'Group password', hint: 'Join a protected group' },
      { id: 'group-options', label: 'Group options', hint: 'Add, leave, transfer' },
      { id: 'confirm', label: 'Confirm action', hint: 'Block or delete' },
      { id: 'info', label: 'Info', hint: 'A short notice' },
      { id: 'schedule', label: 'Schedule call', hint: 'Pick a time and people' },
      { id: 'reschedule', label: 'Reschedule', hint: 'Move an existing call' },
      { id: 'incoming', label: 'Incoming call', hint: 'Accept or decline' },
      { id: 'outgoing', label: 'Outgoing call', hint: 'Calling a contact' },
      { id: 'in-call', label: 'In-call controls', hint: 'Mute, share, invite' },
      { id: 'reactions', label: 'Reactions', hint: 'Who reacted' },
      { id: 'message-info', label: 'Message info', hint: 'Sent, delivered, read' },
      { id: 'delete-message', label: 'Delete message', hint: 'For me or everyone' },
      { id: 'thread', label: 'Thread', hint: 'Replies on a message' },
      { id: 'search', label: 'Search conversation', hint: 'Messages and attachments' },
      { id: 'attach', label: 'Send media', hint: 'Files and a caption' },
      { id: 'unban', label: 'Unban request', hint: 'Ask to rejoin' },
      { id: 'ownership', label: 'Transfer ownership', hint: 'Choose a new owner' },
      { id: 'meeting-admin', label: 'Meeting admin', hint: 'Host controls' },
      { id: 'input', label: 'Rename', hint: 'Edit a group name' },
    ] as ModalItem[]
  ).filter((item) => isKitModalVisible(item.id, config));

const PEOPLE = [
  { id: 'sachin', name: 'Sachin', status: 'Offline', color: '#F6C7A1' },
];

const CALLS = [
  { id: 'c1', name: 'Ravi', note: 'Outgoing call', direction: 'out' as const, kind: 'video' as const, time: 'Mon', duration: '00:00:16', color: '#F6C7A1' },
  { id: 'c2', name: 'Ravi', note: 'Outgoing call', direction: 'out' as const, kind: 'video' as const, time: 'Mon', duration: '00:04:02', color: '#F6C7A1' },
  { id: 'c3', name: 'Ajit', note: 'Missed call', direction: 'missed' as const, kind: 'voice' as const, time: '09:12 PM', duration: '00:00:00', color: '#DDD6FE' },
  { id: 'c4', name: 'Ravi', note: 'Outgoing call', direction: 'out' as const, kind: 'video' as const, time: '09:05 PM', duration: '00:12:40', color: '#F6C7A1' },
  { id: 'c5', name: 'Ravi', note: 'Outgoing call', direction: 'out' as const, kind: 'voice' as const, time: '09:05 PM', duration: '00:03:18', color: '#F6C7A1' },
  { id: 'c6', name: 'Ajit', note: 'Incoming call', direction: 'in' as const, kind: 'voice' as const, time: '08:31 PM', duration: '00:08:05', color: '#DDD6FE' },
  { id: 'c7', name: 'Ajit', note: 'Incoming call', direction: 'in' as const, kind: 'video' as const, time: '08:24 PM', duration: '00:21:11', color: '#DDD6FE' },
  { id: 'c8', name: 'Ravi', note: 'Outgoing call', direction: 'out' as const, kind: 'video' as const, time: '08:16 PM', duration: '00:01:44', color: '#F6C7A1' },
];

const GROUPS = [
  { id: 'h1', name: 'h1', type: 'public' as const, members: 2, unread: 0, color: '#d9f99d', photo: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=120&q=60' },
  { id: 'h2', name: 'h2', type: 'private' as const, members: 2, unread: 5, color: '#F6C7A1', photo: 'https://images.unsplash.com/photo-1508193638397-1c4234db14d8?auto=format&fit=crop&w=120&q=60' },
  { id: 'h2b', name: 'h2', type: 'private' as const, members: 2, unread: 5, color: '#F6C7A1', photo: 'https://images.unsplash.com/photo-1472214103451-9374bd1c798e?auto=format&fit=crop&w=120&q=60' },
  { id: 'h3', name: 'h3', type: 'public' as const, members: 2, unread: 5, color: '#BFDBFE', photo: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=120&q=60' },
];

const SCHEDULED = [
  {
    id: 'he',
    title: 'he',
    when: 'Sep 25, 2026 · 1:49 PM – 2:04 PM',
    status: 'ongoing' as const,
    type: 'video' as const,
    people: '2 participants · Host: Ravi',
    invited: '2 invited · Host: Ravi',
    initial: 'h',
    color: '#F6C7A1',
  },
];

const MEMBERS = [
  { name: 'You', role: 'Owner', color: '#DBEAFE' },
  { name: 'Mira Sharma', role: 'Admin', color: '#DDD6FE' },
  { name: 'Arjun Mehta', role: 'Member', color: '#FED7AA' },
  { name: 'Neel Kapoor', role: 'Moderator', color: '#FDE68A' },
];

const labelSx = (tokens: ThemeTokens) => ({
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: tokens.muted,
  fontFamily: tokens.fontFamily,
  textAlign: 'left' as const,
});

const textSx = (tokens: ThemeTokens, sizeOffset = 0, weight = 500) => ({
  fontFamily: tokens.fontFamily,
  fontSize: tokens.fontSize + sizeOffset,
  fontWeight: weight,
  color: tokens.primary,
  lineHeight: 1.35,
});

const Initials: React.FC<{ name: string; color: string; size?: number; tokens: ThemeTokens; group?: boolean }> = ({
  name,
  color,
  size = 42,
  tokens,
  group,
}) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      bgcolor: color,
      color: tokens.primary,
      fontFamily: tokens.fontFamily,
      fontWeight: 700,
      fontSize: size * 0.32,
      flexShrink: 0,
    }}
  >
    {group ? (
      <GroupsOutlinedIcon sx={{ fontSize: size * 0.46 }} />
    ) : (
      name
        .split(' ')
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    )}
  </Avatar>
);

const ScreenHeader: React.FC<{
  tokens: ThemeTokens;
  kicker: string;
  title: string;
  onBack?: () => void;
  action?: React.ReactNode;
}> = ({ tokens, kicker, title, onBack, action }) => (
  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ px: 2.25, pt: 2, pb: 1.25 }}>
    <Box sx={{ textAlign: 'left', minWidth: 0 }}>
      <Typography sx={labelSx(tokens)}>{kicker}</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
        {onBack && (
          <Box
            onClick={onBack}
            sx={{ cursor: 'pointer', color: tokens.primary, fontSize: tokens.fontSize - 1, fontFamily: tokens.fontFamily, fontWeight: 600 }}
          >
            Back
          </Box>
        )}
        <Typography sx={{ ...textSx(tokens, 5, 700), lineHeight: 1.15 }}>{title}</Typography>
      </Stack>
    </Box>
    {action}
  </Stack>
);

const IconAction: React.FC<{ tokens: ThemeTokens; children: React.ReactNode; onClick?: () => void }> = ({
  tokens,
  children,
  onClick,
}) => (
  <Box
    onClick={onClick}
    sx={{
      width: 32,
      height: 32,
      borderRadius: '10px',
      border: `1px solid ${withAlpha(tokens.brand, '35')}`,
      color: tokens.brand,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: onClick ? 'pointer' : 'default',
      flexShrink: 0,
      backgroundColor: tokens.surface,
    }}
  >
    {children}
  </Box>
);

const selectedRowSx = (tokens: ThemeTokens, active: boolean) => ({
  position: 'relative' as const,
  overflow: 'hidden',
  borderRadius: '16px',
  backgroundColor: active ? (tokens.isDark ? '#172033' : '#F3F6FB') : 'transparent',
  border: '1px solid transparent',
  transition: 'background-color 140ms ease',
  '&:hover': {
    backgroundColor: active ? (tokens.isDark ? '#172033' : '#F3F6FB') : tokens.isDark ? '#111827' : '#F7F8FB',
  },
  '&::before': active
    ? {
        content: '""',
        position: 'absolute' as const,
        left: 0,
        top: 0,
        bottom: 0,
        width: 5,
        borderRadius: '16px 0 0 16px',
        backgroundColor: tokens.brand,
      }
    : undefined,
});

const Row: React.FC<{
  tokens: ThemeTokens;
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ tokens, active, onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 1.4,
      px: 1.4,
      py: 1.25,
      borderRadius: '12px',
      cursor: onClick ? 'pointer' : 'default',
      backgroundColor: active ? tokens.softBrand : 'transparent',
      border: `1px solid ${active ? withAlpha(tokens.brand, '28') : 'transparent'}`,
      overflow: 'hidden',
      '&:hover': onClick
        ? { backgroundColor: active ? tokens.softBrand : withAlpha(tokens.brand, '08') }
        : undefined,
      '&::before': active
        ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: tokens.brand,
          }
        : undefined,
    }}
  >
    {children}
  </Box>
);

const Pill: React.FC<{ tokens: ThemeTokens; label: string; tone?: string }> = ({ tokens, label, tone }) => (
  <Box
    sx={{
      px: 0.9,
      py: 0.25,
      borderRadius: '999px',
      backgroundColor: tone ? withAlpha(tone, '18') : tokens.softBrand,
      color: tone || tokens.brand,
      fontSize: Math.max(10, tokens.fontSize - 2.5),
      fontWeight: 700,
      fontFamily: tokens.fontFamily,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const Field: React.FC<{ tokens: ThemeTokens; label: string; value: string }> = ({ tokens, label, value }) => (
  <Box sx={{ mb: 1.4 }}>
    <Typography sx={{ ...textSx(tokens, -1.5, 600), mb: 0.6 }}>{label}</Typography>
    <Box
      sx={{
        px: 1.3,
        py: 1,
        borderRadius: '10px',
        border: `1px solid ${tokens.border}`,
        backgroundColor: tokens.isDark ? '#10192c' : tokens.surface,
        color: tokens.secondary,
        fontFamily: tokens.fontFamily,
        fontSize: tokens.fontSize - 1,
        textAlign: 'left',
      }}
    >
      {value}
    </Box>
  </Box>
);

const BrandButton: React.FC<{ tokens: ThemeTokens; label: string; onClick?: () => void; full?: boolean }> = ({
  tokens,
  label,
  onClick,
  full,
}) => (
    <Box
    onClick={(event) => {
      event.stopPropagation();
      onClick?.();
    }}
    sx={{
      px: 1.6,
      py: 0.9,
      borderRadius: '10px',
      backgroundColor: tokens.brand,
      color: '#fff',
      fontFamily: tokens.fontFamily,
      fontWeight: 700,
      fontSize: tokens.fontSize - 1,
      cursor: 'pointer',
      textAlign: 'center',
      width: full ? '100%' : 'auto',
    }}
  >
    {label}
  </Box>
);

const QuietButton: React.FC<{ tokens: ThemeTokens; label: string; onClick?: () => void; danger?: boolean }> = ({
  tokens,
  label,
  onClick,
  danger,
}) => (
    <Box
    onClick={(event) => {
      event.stopPropagation();
      onClick?.();
    }}
    sx={{
      px: 1.2,
      py: 0.7,
      borderRadius: '10px',
      border: `1px solid ${tokens.border}`,
      color: danger ? '#df4b54' : tokens.primary,
      fontFamily: tokens.fontFamily,
      fontWeight: 600,
      fontSize: tokens.fontSize - 1.5,
      cursor: 'pointer',
      backgroundColor: tokens.surface,
      textAlign: 'center',
    }}
  >
    {label}
  </Box>
);

const ScheduleFormBody: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onClose?: () => void;
  title?: string;
  submitLabel?: string;
}> = ({ tokens, config, onClose, title = 'Schedule a Call', submitLabel = 'Schedule call' }) => {
  const [kind, setKind] = useState<'voice' | 'video'>(showVideoCalls(config) ? 'video' : 'voice');
  const [slot, setSlot] = useState(15);
  const [people, setPeople] = useState<string[]>(['Ajit']);
  const [remindersOn, setRemindersOn] = useState(true);
  const [reminder, setReminder] = useState(5);
  const voice = showVoiceCalls(config);
  const video = showVideoCalls(config);
  const inputSx = {
    height: 42,
    px: 1.4,
    borderRadius: '10px',
    border: `1px solid ${tokens.border}`,
    backgroundColor: 'transparent',
    color: tokens.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    fontFamily: tokens.fontFamily,
    fontSize: tokens.fontSize - 1,
  } as const;
  const choiceSx = (active: boolean) => ({
    flex: 1,
    height: 40,
    borderRadius: '10px',
    border: active ? 'none' : `1px solid ${tokens.border}`,
    backgroundColor: active ? tokens.brand : 'transparent',
    color: active ? '#fff' : tokens.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.7,
    cursor: 'pointer',
    fontFamily: tokens.fontFamily,
    fontWeight: 600,
    fontSize: tokens.fontSize - 1,
  });
  const star = <Box component="span" sx={{ color: tokens.primary }}> *</Box>;

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        width: '100%',
        maxWidth: 460,
        maxHeight: '92%',
        overflow: 'auto',
        backgroundColor: tokens.isDark ? '#161e30' : tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: '16px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        p: 2.25,
        textAlign: 'left',
        fontFamily: tokens.fontFamily,
        ...hideScrollbarSx,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.6 }}>
        <Box>
          <Typography sx={{ ...labelSx(tokens), color: tokens.brand, mb: 0.45 }}>Calls</Typography>
          <Typography sx={{ ...textSx(tokens, 4, 700), lineHeight: 1.15 }}>{title}</Typography>
          <Typography sx={{ ...textSx(tokens, -1.5, 500), color: tokens.secondary, mt: 0.35 }}>Set up a meeting for later</Typography>
        </Box>
        {onClose && (
          <Box onClick={onClose} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Stack>

      <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.7 }}>Meeting Title{star}</Typography>
      <Box sx={{ ...inputSx, color: tokens.muted, mb: 1.5 }}>e.g. Planning Session</Box>

      <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.7 }}>Description{star}</Typography>
      <Box sx={{ ...inputSx, height: 72, alignItems: 'flex-start', py: 1.1, color: tokens.muted, mb: 1.6 }}>What's this meeting about?</Box>

      {(voice || video) && (
        <Box sx={{ mb: 1.6 }}>
          <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.8 }}>Call Type</Typography>
          <Stack direction="row" spacing={1}>
            {video && (
              <Box onClick={() => setKind('video')} sx={choiceSx(kind === 'video')}>
                <VideocamOutlinedIcon sx={{ fontSize: 16 }} /> Video
              </Box>
            )}
            {voice && (
              <Box onClick={() => setKind('voice')} sx={choiceSx(kind === 'voice')}>
                <CallOutlinedIcon sx={{ fontSize: 16 }} /> Audio
              </Box>
            )}
          </Stack>
        </Box>
      )}

      <Stack direction="row" spacing={1.2} sx={{ mb: 1.6 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.7 }}>Start Date</Typography>
          <Box sx={inputSx}>
            <span>19-10-2026</span>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: tokens.secondary }} />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.7 }}>Start Time</Typography>
          <Box sx={inputSx}>
            <span>23:15</span>
            <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: tokens.secondary }} />
          </Box>
        </Box>
      </Stack>

      <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.8 }}>Duration</Typography>
      <Stack direction="row" spacing={0.8} sx={{ mb: 1.6 }}>
        {[
          [15, '15 min'],
          [30, '30 min'],
          [45, '45 min'],
          [60, '1 hr'],
        ].map(([minutes, label]) => (
          <Box key={label} onClick={() => setSlot(Number(minutes))} sx={choiceSx(slot === minutes)}>
            {label}
          </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={1.2} sx={{ mb: 1.6 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.7 }}>Timezone</Typography>
          <Box sx={inputSx}>Asia/Calcutta</Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.7 }}>Grace Time (minutes)</Typography>
          <Box sx={inputSx}>5</Box>
        </Box>
      </Stack>

      <Typography sx={{ ...textSx(tokens, -1.2, 700), mb: 0.8 }}>Participants{star}</Typography>
      <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: '12px', mb: 1.8, overflow: 'hidden' }}>
        {['Ajit', 'Sachin', 'Ravi'].map((name) => {
          const checked = people.includes(name);
          return (
            <Stack
              key={name}
              direction="row"
              spacing={1.1}
              alignItems="center"
              onClick={() => setPeople((current) => (current.includes(name) ? current.filter((item) => item !== name) : [...current, name]))}
              sx={{ px: 1.4, py: 1, cursor: 'pointer' }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '4px',
                  border: checked ? 'none' : `1.5px solid ${tokens.border}`,
                  backgroundColor: checked ? '#d7dbe3' : 'transparent',
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {checked && <CheckIcon sx={{ fontSize: 14 }} />}
              </Box>
              <Typography sx={{ ...textSx(tokens, -0.5, 500) }}>{name}</Typography>
            </Stack>
          );
        })}
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: remindersOn ? 1 : 1.8 }}>
        <Box>
          <Typography sx={{ ...textSx(tokens, -1.2, 700) }}>Reminders</Typography>
          <Typography sx={{ ...textSx(tokens, -2.2, 500), color: tokens.secondary }}>Notify participants before the call</Typography>
        </Box>
        <Box
          onClick={() => setRemindersOn((on) => !on)}
          sx={{
            width: 42,
            height: 24,
            borderRadius: '999px',
            backgroundColor: remindersOn ? tokens.brand : tokens.border,
            position: 'relative',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 3,
              left: remindersOn ? 21 : 3,
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: '#fff',
            }}
          />
        </Box>
      </Stack>
      {remindersOn && (
        <Stack direction="row" spacing={0.7} sx={{ mb: 2 }}>
          {[5, 10, 15, 30, 60].map((minutes) => {
            const active = reminder === minutes;
            return (
              <Box
                key={minutes}
                onClick={() => setReminder(minutes)}
                sx={{
                  px: 1.15,
                  py: 0.45,
                  borderRadius: '999px',
                  cursor: 'pointer',
                  backgroundColor: active ? tokens.brand : 'transparent',
                  border: active ? 'none' : `1px solid ${tokens.border}`,
                  color: active ? '#fff' : tokens.primary,
                  fontFamily: tokens.fontFamily,
                  fontWeight: 600,
                  fontSize: tokens.fontSize - 2,
                }}
              >
                {minutes} min
              </Box>
            );
          })}
        </Stack>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {onClose && (
          <Box onClick={onClose} sx={{ px: 1.8, py: 0.85, borderRadius: '10px', border: `1px solid ${tokens.border}`, cursor: 'pointer' }}>
            <Typography sx={{ ...textSx(tokens, -1, 600) }}>Cancel</Typography>
          </Box>
        )}
        <Box onClick={onClose} sx={{ px: 1.8, py: 0.85, borderRadius: '10px', backgroundColor: tokens.brand, cursor: 'pointer' }}>
          <Typography sx={{ ...textSx(tokens, -1, 700), color: '#fff' }}>{submitLabel}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const QuickActions: React.FC<{
  tokens: ThemeTokens;
  items: Array<{ label: string; icon: React.ReactNode; onClick?: () => void }>;
}> = ({ tokens, items }) => {
  if (items.length === 0) return null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 0.8, mb: 2 }}>
      {items.map((item) => (
        <Box
          key={item.label}
          onClick={item.onClick}
          sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: '12px',
            py: 1,
            textAlign: 'center',
            cursor: 'pointer',
            color: tokens.secondary,
            backgroundColor: tokens.surface,
            '&:hover': {
              color: tokens.brand,
              borderColor: tokens.brand,
              backgroundColor: withAlpha(tokens.brand, '12'),
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.4, color: 'inherit' }}>{item.icon}</Box>
          <Typography sx={{ fontSize: Math.max(10, tokens.fontSize - 3), fontWeight: 700, fontFamily: tokens.fontFamily, color: 'inherit' }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const PeerDetail: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onNavigate: (id: KitScreenId) => void;
  onOpenModal: (id: KitModalId) => void;
}> = ({ tokens, config, onNavigate, onOpenModal }) => {
  const actions = [
    { label: 'Chat', icon: <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />, onClick: () => onNavigate('peer') },
  ];
  if (config.call_feature.is_one_to_one_voice_calls) {
    actions.push({ label: 'Voice', icon: <CallOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') });
  }
  if (config.call_feature.is_one_to_one_video_calls) {
    actions.push({ label: 'Video', icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') });
  }
  if (config.chat_feature.is_search_message) {
    actions.push({ label: 'Search', icon: <SearchOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('search') });
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', ...hideScrollbarSx }}>
      <ScreenHeader tokens={tokens} kicker="Conversation details" title="Mira Sharma" onBack={() => onNavigate('peer')} />
      <Box sx={{ px: 2.25, pb: 2.5, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Initials name="Mira Sharma" color="#DDD6FE" size={72} tokens={tokens} />
        </Box>
        <Typography sx={textSx(tokens, 3, 700)}>Mira Sharma</Typography>
        <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1 }}>
          {config.chat_feature.is_user_presence ? 'Online now' : 'Last seen recently'}
        </Typography>
        {config.chat_feature.is_user_presence && <Pill tokens={tokens} label="Online" tone="#118d77" />}
        <Box sx={{ mt: 2, textAlign: 'left' }}>
          <Typography sx={{ ...labelSx(tokens), mb: 1 }}>Quick actions</Typography>
          <QuickActions tokens={tokens} items={actions} />
          {config.chat_feature.is_media_file_sharing && (
            <>
              <Typography sx={{ ...labelSx(tokens), mb: 1 }}>Shared files</Typography>
              <Row tokens={tokens}>
                <DescriptionOutlinedIcon sx={{ color: tokens.brand }} />
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                  <Typography sx={textSx(tokens, -1, 600)}>Conversation brief.pdf</Typography>
                  <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>Today · 1.8 MB</Typography>
                </Box>
              </Row>
            </>
          )}
          {(config.chat_feature.is_block_user || config.chat_feature.is_delete_conversation) && (
            <>
              <Typography sx={{ ...labelSx(tokens), mt: 2, mb: 1 }}>Privacy</Typography>
              <Stack spacing={1}>
                {config.chat_feature.is_block_user && (
                  <QuietButton tokens={tokens} label="Block contact" danger onClick={() => onOpenModal('confirm')} />
                )}
                {config.chat_feature.is_delete_conversation && (
                  <QuietButton tokens={tokens} label="Delete conversation" danger onClick={() => onOpenModal('confirm')} />
                )}
              </Stack>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const GroupDetail: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onNavigate: (id: KitScreenId) => void;
  onOpenModal: (id: KitModalId) => void;
}> = ({ tokens, config, onNavigate, onOpenModal }) => {
  const [tab, setTab] = useState<'members' | 'banned'>('members');
  const actions = [{ label: 'Chat', icon: <GroupsOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onNavigate('group') }];
  if (config.call_feature.is_group_voice_calls) {
    actions.push({ label: 'Voice', icon: <CallOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') });
  }
  if (config.call_feature.is_group_video_calls) {
    actions.push({ label: 'Video', icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') });
  }
  if (config.chat_feature.is_search_message) {
    actions.push({ label: 'Search', icon: <SearchOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('search') });
  }
  const showBanned = config.chat_feature.is_ban_user;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', ...hideScrollbarSx }}>
      <ScreenHeader
        tokens={tokens}
        kicker="Group details"
        title="Launch team"
        onBack={() => onNavigate('group')}
        action={<IconAction tokens={tokens} onClick={() => onOpenModal('group-options')}><AddIcon sx={{ fontSize: 18 }} /></IconAction>}
      />
      <Box sx={{ px: 2.25, pb: 2.5 }}>
        <Stack alignItems="center" spacing={0.6} sx={{ mb: 2 }}>
          <Initials name="Launch team" color="#BBF7D0" size={72} tokens={tokens} group />
          <Typography sx={textSx(tokens, 3, 700)}>Launch team</Typography>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary }}>8 members · private group</Typography>
        </Stack>
        <QuickActions tokens={tokens} items={actions} />
        <Stack direction="row" spacing={1} sx={{ mb: 1.2 }}>
          <Box onClick={() => setTab('members')} sx={{ cursor: 'pointer' }}>
            <Pill tokens={tokens} label="Members" tone={tab === 'members' ? tokens.brand : tokens.secondary} />
          </Box>
          {showBanned && (
            <Box onClick={() => setTab('banned')} sx={{ cursor: 'pointer' }}>
              <Pill tokens={tokens} label="Banned" tone={tab === 'banned' ? tokens.brand : tokens.secondary} />
            </Box>
          )}
        </Stack>
        {tab === 'banned' && showBanned ? (
          <Row tokens={tokens} onClick={() => onOpenModal('unban')}>
            <Initials name="Sam Lee" color="#FECACA" size={36} tokens={tokens} />
            <Box sx={{ flex: 1, textAlign: 'left' }}>
              <Typography sx={textSx(tokens, -1, 600)}>Sam Lee</Typography>
              <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>Banned · request pending</Typography>
            </Box>
          </Row>
        ) : (
          <Stack spacing={0.6}>
            {MEMBERS.map((member) => (
              <Row key={member.name} tokens={tokens}>
                <Initials name={member.name} color={member.color} size={36} tokens={tokens} />
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography sx={textSx(tokens, -1, 600)}>{member.name}</Typography>
                  <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>{member.role}</Typography>
                </Box>
                <Stack direction="row" spacing={0.6}>
                  {config.chat_feature.is_mute_user && member.role === 'Member' && (
                    <QuietButton tokens={tokens} label="Mute" onClick={() => onOpenModal('confirm')} />
                  )}
                  {config.chat_feature.is_ban_user && member.role === 'Member' && (
                    <QuietButton tokens={tokens} label="Ban" danger onClick={() => onOpenModal('confirm')} />
                  )}
                </Stack>
              </Row>
            ))}
          </Stack>
        )}
        {config.chat_feature.is_delete_conversation && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <QuietButton tokens={tokens} label="Delete chat" danger onClick={() => onOpenModal('confirm')} />
            <QuietButton tokens={tokens} label="Delete and exit" danger onClick={() => onOpenModal('confirm')} />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const ProfileFooter: React.FC<{
  tokens: ThemeTokens;
  name?: string;
  color?: string;
  statusColor?: string;
  showPresence?: boolean;
}> = ({ tokens, name = 'Ajit', color = '#DDD6FE', statusColor, showPresence = true }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ px: 2, py: 1.4, borderTop: `1px solid ${tokens.border}`, display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
        <Avatar sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: color, color: '#3f342b', fontFamily: tokens.fontFamily, fontWeight: 700, fontSize: 14 }}>
          {name.slice(0, 1).toUpperCase()}
        </Avatar>
        {showPresence && (
          <Box sx={{ position: 'absolute', right: -1, bottom: -1, width: 10, height: 10, borderRadius: '50%', backgroundColor: '#22C55E', border: `2px solid ${tokens.surface}` }} />
        )}
      </Box>
      <Box sx={{ flex: 1, textAlign: 'left' }}>
        <Typography sx={{ ...textSx(tokens, -1, 700) }}>{name}</Typography>
        {showPresence && (
          <Typography sx={{ ...textSx(tokens, -2.2, 500), color: statusColor || tokens.secondary }}>Available</Typography>
        )}
      </Box>
      <Box onClick={() => setOpen((value) => !value)} sx={{ position: 'relative', cursor: 'pointer', display: 'flex', color: tokens.primary }}>
        {open && (
          <Box
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 'calc(100% + 8px)',
              zIndex: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1.05,
              minWidth: 132,
              borderRadius: '12px',
              border: `1px solid ${tokens.border}`,
              backgroundColor: tokens.isDark ? '#141c2e' : tokens.surface,
              boxShadow: '0 14px 32px rgba(0,0,0,0.16)',
              color: '#f05252',
              cursor: 'pointer',
            }}
          >
            <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: tokens.fontSize, fontFamily: tokens.fontFamily, color: '#f05252' }}>Logout</Typography>
          </Box>
        )}
        <MoreHorizIcon sx={{ fontSize: 18 }} />
      </Box>
    </Box>
  );
};

const PeopleScreen: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  compact?: boolean;
  onNavigate: (id: KitScreenId, targetId?: string) => void;
}> = ({ tokens, config, compact, onNavigate }) => {
  const [selectedId, setSelectedId] = useState(PEOPLE[0]?.id ?? '');
  return (
  <Box sx={{ height: '100%', minWidth: 0, display: 'flex' }}>
    <Box
      sx={{
        width: compact ? '100%' : { xs: 268, lg: 300 },
        minWidth: compact ? 0 : { xs: 248, lg: 280 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: compact ? 'none' : `1px solid ${tokens.border}`,
        backgroundColor: tokens.surface,
        flexShrink: 0,
      }}
    >
      <Box sx={{ px: 2.25, pt: 2.25, pb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.75 }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ ...labelSx(tokens), mb: 0.45 }}>Communications</Typography>
            <Typography sx={{ ...textSx(tokens, 6, 700), lineHeight: 1.1 }}>People</Typography>
          </Box>
          {config.call_feature.is_schedule_calls && (
            <Box
              onClick={() => onNavigate('schedule-form')}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '10px',
                border: `1.5px solid ${withAlpha(tokens.brand, '35')}`,
                color: tokens.brand,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
          )}
        </Stack>
        {config.chat_feature.is_search_message && (
          <Box
            sx={{
              height: 40,
              px: 1.5,
              borderRadius: '8px',
              backgroundColor: tokens.isDark ? '#1F2937' : '#F3F4F6',
              border: `1px solid ${tokens.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: tokens.muted,
            }}
          >
            <SearchOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.muted, fontFamily: tokens.fontFamily }}>Search chats</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ borderTop: `1px solid ${tokens.border}` }} />
      <Typography sx={{ ...labelSx(tokens), px: 2.25, pt: 1.5, pb: 0.75 }}>Your contacts</Typography>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1.5, ...hideScrollbarSx }}>
        <Stack spacing={0.4}>
          {PEOPLE.map((person) => (
            <Box
              key={person.id}
              onClick={() => {
                setSelectedId(person.id);
                onNavigate('peer', person.id);
              }}
              sx={{
                ...selectedRowSx(tokens, person.id === selectedId),
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.1,
                py: 1,
                cursor: 'pointer',
              }}
            >
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  bgcolor: person.color,
                  color: '#3f342b',
                  fontFamily: tokens.fontFamily,
                  fontWeight: 700,
                  fontSize: 16,
                }}
              >
                {person.name.slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Typography noWrap sx={{ ...textSx(tokens, -0.5, 700) }}>{person.name}</Typography>
                {config.chat_feature.is_user_presence && (
                  <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary, mt: 0.15 }}>
                    {person.status}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
      <ProfileFooter tokens={tokens} showPresence={config.chat_feature.is_user_presence} />
    </Box>
    {!compact && (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tokens.isDark ? tokens.surface : tokens.chatBg,
          px: 3,
        }}
      >
        <Stack alignItems="center" spacing={1.5} sx={{ maxWidth: 360, textAlign: 'center' }}>
          <ChatBubbleIcon sx={{ fontSize: 52, color: tokens.isDark ? '#8b95a8' : tokens.muted }} />
          <Typography sx={{ ...textSx(tokens, 6, 700), lineHeight: 1.25 }}>Welcome to your Conversations</Typography>
          <Typography sx={{ ...textSx(tokens, -0.5, 500), color: tokens.secondary, lineHeight: 1.55 }}>
            Select a chat from the list to start exploring your messages or begin a new conversation
          </Typography>
        </Stack>
      </Box>
    )}
  </Box>
  );
};

const CallsScreen: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  compact?: boolean;
  onOpenModal: (id: KitModalId) => void;
}> = ({ tokens, config, compact, onOpenModal }) => {
  const rows = CALLS.filter((call) => (call.kind === 'video' ? showVideoCalls(config) : showVoiceCalls(config)));
  const [selectedId, setSelectedId] = useState(rows[0]?.id || '');
  const [detailTab, setDetailTab] = useState<'participants' | 'recording' | 'history'>('participants');
  const [showDetail, setShowDetail] = useState(!compact);
  const selected = rows.find((call) => call.id === selectedId) || rows[0];
  const detailVisible = Boolean(selected) && (compact ? showDetail : true);
  const statusLabel = selected?.direction === 'missed' ? 'Missed' : selected?.direction === 'in' ? 'Incoming' : 'Outgoing';
  const kindLabel = selected?.kind === 'video' ? 'Video' : 'Voice';

  const list = (
    <Box
      sx={{
        width: detailVisible && !compact ? { xs: 268, lg: 300 } : '100%',
        minWidth: detailVisible && !compact ? { xs: 248, lg: 280 } : 0,
        height: '100%',
        display: detailVisible && compact ? 'none' : 'flex',
        flexDirection: 'column',
        borderRight: detailVisible && !compact ? `1px solid ${tokens.border}` : 'none',
        backgroundColor: tokens.surface,
        flexShrink: 0,
      }}
    >
      <Box sx={{ px: 2.25, pt: 2.25, pb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.75 }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ ...labelSx(tokens), mb: 0.45 }}>Communications</Typography>
            <Typography sx={{ ...textSx(tokens, 6, 700), lineHeight: 1.1 }}>Calls</Typography>
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              border: `1.5px solid ${withAlpha(tokens.brand, '35')}`,
              color: tokens.brand,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Box>
        </Stack>
        {config.chat_feature.is_search_message && (
          <Box
            sx={{
              height: 40,
              px: 1.5,
              borderRadius: '8px',
              backgroundColor: tokens.isDark ? '#1F2937' : '#F3F4F6',
              border: `1px solid ${tokens.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: tokens.muted,
            }}
          >
            <SearchOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.muted, fontFamily: tokens.fontFamily }}>Search chats</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ borderTop: `1px solid ${tokens.border}` }} />
      <Typography sx={{ ...labelSx(tokens), px: 2.25, pt: 1.5, pb: 0.75 }}>Recent calls</Typography>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1.5, ...hideScrollbarSx }}>
        {rows.length === 0 && (
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, px: 1 }}>No calls for the enabled call types.</Typography>
        )}
        <Stack spacing={0.6}>
          {rows.map((call) => {
            const missed = call.direction === 'missed';
            const active = call.id === selected?.id;
            return (
              <Box
                key={call.id}
                onClick={() => {
                  setSelectedId(call.id);
                  setDetailTab('participants');
                  setShowDetail(true);
                }}
                sx={{
                  ...selectedRowSx(tokens, active),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.25,
                  py: 1.15,
                  cursor: 'pointer',
                }}
              >
                <Initials name={call.name} color={call.color} size={40} tokens={tokens} />
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <Typography noWrap sx={{ ...textSx(tokens, -0.5, 700) }}>{call.name}</Typography>
                  <Stack direction="row" spacing={0.45} alignItems="center" sx={{ mt: 0.25 }}>
                    <CallOutlinedIcon sx={{ fontSize: 13, color: missed ? '#df4b54' : tokens.secondary }} />
                    <Typography sx={{ ...textSx(tokens, -2, 500), color: missed ? '#df4b54' : tokens.secondary }}>{call.note}</Typography>
                  </Stack>
                </Box>
                <Typography sx={{ ...textSx(tokens, -2.2, 500), color: tokens.secondary, flexShrink: 0 }}>{call.time}</Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
      <ProfileFooter tokens={tokens} name="Ravi" color="#F6C7A1" statusColor="#22C55E" showPresence={config.chat_feature.is_user_presence} />
    </Box>
  );

  const detail = selected && detailVisible && (
    <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: tokens.isDark ? tokens.surface : tokens.chatBg }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${tokens.border}` }}>
        <Box
          onClick={() => compact && setShowDetail(false)}
          sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.secondary, cursor: compact ? 'pointer' : 'default' }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </Box>
        <Initials name={selected.name} color={selected.color} size={36} tokens={tokens} />
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <Typography noWrap sx={{ ...textSx(tokens, 0, 700) }}>{selected.name}</Typography>
          <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>{selected.note}</Typography>
        </Box>
        {showVoiceCalls(config) && (
          <Box onClick={() => onOpenModal('outgoing')} sx={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.primary, cursor: 'pointer' }}>
            <CallOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
        )}
        {showVideoCalls(config) && (
          <Box onClick={() => onOpenModal('outgoing')} sx={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.primary, cursor: 'pointer' }}>
            <VideocamOutlinedIcon sx={{ fontSize: 21 }} />
          </Box>
        )}
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, ...hideScrollbarSx }}>
        <Stack alignItems="center" sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '18px',
              backgroundColor: selected.color,
              color: tokens.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: tokens.fontFamily,
              fontWeight: 700,
              fontSize: 26,
              mb: 2,
            }}
          >
            {selected.name.slice(0, 1)}
          </Box>
          <Typography sx={{ ...labelSx(tokens), mb: 0.6 }}>Call details</Typography>
          <Typography sx={{ ...textSx(tokens, 8, 700), letterSpacing: '-0.03em' }}>
            {statusLabel} {kindLabel.toLowerCase()} call
          </Typography>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mt: 0.4, mb: 2.5 }}>{selected.time}</Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.25, width: '100%', mb: 2.5 }}>
            {[
              ['Duration', selected.duration],
              ['Type', kindLabel],
              ['Status', statusLabel],
            ].map(([label, value]) => (
              <Box key={label} sx={{ textAlign: 'left', px: 1.6, py: 1.35, borderRadius: '12px', backgroundColor: tokens.isDark ? '#141c2e' : tokens.panel, border: `1px solid ${tokens.border}` }}>
                <Typography sx={{ ...labelSx(tokens), mb: 0.7 }}>{label}</Typography>
                <Typography sx={{ ...textSx(tokens, 1, 700) }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Stack direction="row" justifyContent="center" spacing={4} sx={{ width: '100%', borderBottom: `1px solid ${tokens.border}`, mb: 2.25 }}>
            {(['participants', 'recording', 'history'] as const).map((tab) => {
              const active = detailTab === tab;
              return (
                <Box key={tab} onClick={() => setDetailTab(tab)} sx={{ position: 'relative', pb: 1.1, cursor: 'pointer' }}>
                  <Typography sx={{ fontFamily: tokens.fontFamily, fontSize: tokens.fontSize - 0.5, fontWeight: active ? 700 : 500, color: active ? tokens.brand : tokens.secondary, textTransform: 'capitalize' }}>
                    {tab}
                  </Typography>
                  {active && <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, backgroundColor: tokens.brand }} />}
                </Box>
              );
            })}
          </Stack>

          {detailTab === 'participants' && (
            <Box sx={{ width: '100%', textAlign: 'left' }}>
              <Typography sx={{ ...labelSx(tokens), mb: 0.8 }}>Participants</Typography>
              <Typography sx={{ ...textSx(tokens, 1, 700), mb: 1.5 }}>2 people on this call</Typography>
              <Stack spacing={1.25}>
                {[
                  { name: selected.name, role: 'Participant', color: selected.color, host: false },
                  { name: 'Ravi', role: 'Host', color: '#F6C7A1', host: true },
                ].map((person) => (
                  <Stack key={person.role} direction="row" spacing={1.25} alignItems="center">
                    <Initials name={person.name} color={person.color} size={36} tokens={tokens} />
                    <Box>
                      <Typography sx={{ ...textSx(tokens, -0.5, 700) }}>{person.name}</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {person.host && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22C55E' }} />}
                        <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>{person.role}</Typography>
                      </Stack>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
          {detailTab === 'recording' && (
            <Typography sx={{ ...textSx(tokens, -0.5, 500), color: tokens.secondary, width: '100%', textAlign: 'left' }}>
              No recording for this call.
            </Typography>
          )}
          {detailTab === 'history' && (
            <Box sx={{ width: '100%', textAlign: 'left' }}>
              <Typography sx={{ ...textSx(tokens, -0.5, 600) }}>{selected.note}</Typography>
              <Typography sx={{ ...textSx(tokens, -1.5, 500), color: tokens.secondary }}>{selected.time} · {selected.duration}</Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', minWidth: 0, backgroundColor: tokens.surface }}>
      {list}
      {detail}
    </Box>
  );
};

const MessageComposerField: React.FC<{
  tokens: ThemeTokens;
  voiceEnabled: boolean;
  initialDraft?: string;
  mentionsEnabled?: boolean;
  mentions?: Array<{ name: string; color: string }>;
  withEmoji?: boolean;
}> = ({ tokens, voiceEnabled, initialDraft = '', mentionsEnabled = false, mentions = [], withEmoji = false }) => {
  const [draft, setDraft] = useState(initialDraft);
  const [mentionCursor, setMentionCursor] = useState(0);
  const [mentionClosedQuery, setMentionClosedQuery] = useState<string | null>(null);
  const hasText = draft.length > 0;
  const showSend = !voiceEnabled || hasText;
  const mentionMatch = mentionsEnabled ? draft.match(/(?:^|\s)@([^\s@]*)$/) : null;
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const mentionHint = tokens.isDark ? '#94A3B8' : '#98A2B3';
  const mentionOptions =
    mentionQuery === null
      ? []
      : [
          {
            key: 'everyone',
            name: 'Everyone',
            color: '#7C6CF6',
            everyone: true,
            subtitle: 'Notify everyone in this conversation',
          },
          ...mentions.map((member) => ({
            key: member.name,
            name: member.name,
            color: member.color,
            everyone: false,
            subtitle: 'Member',
          })),
        ].filter((member) => member.name.toLowerCase().includes(mentionQuery));
  const mentionOpen = mentionQuery !== null && mentionClosedQuery !== mentionQuery && mentionOptions.length > 0;
  const mentionActive = Math.min(mentionCursor, Math.max(mentionOptions.length - 1, 0));

  useEffect(() => {
    setMentionCursor(0);
  }, [mentionQuery]);

  const insertMention = (name: string) => {
    setDraft((current) => current.replace(/(?:^|\s)@([^\s@]*)$/, (chunk) => `${chunk.startsWith(' ') ? ' ' : ''}@${name} `));
  };

  const onMentionKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!mentionOpen) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionCursor((index) => (Math.min(index, mentionOptions.length - 1) + 1) % mentionOptions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionCursor((index) => (Math.min(index, mentionOptions.length - 1) - 1 + mentionOptions.length) % mentionOptions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      insertMention(mentionOptions[mentionActive].name);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setMentionClosedQuery(mentionQuery);
    }
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
      {mentionOpen && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 'calc(100% + 10px)',
            width: 'min(340px, 100%)',
            borderRadius: '18px',
            backgroundColor: tokens.surface,
            border: `1px solid ${tokens.isDark ? tokens.border : '#E7ECF2'}`,
            boxShadow: tokens.isDark ? '0 16px 40px rgba(0, 0, 0, 0.35)' : '0 16px 40px rgba(15, 23, 42, 0.12)',
            zIndex: 6,
            overflow: 'hidden',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.75, pt: 1.4, pb: 0.45 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 400, letterSpacing: '0.08em', color: mentionHint, fontFamily: tokens.fontFamily }}>
              MENTION SOMEONE
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 400, color: mentionHint, fontFamily: tokens.fontFamily }}>
              {mentionOptions.length} {mentionOptions.length === 1 ? 'match' : 'matches'}
            </Typography>
          </Stack>
          <Box sx={{ px: 0.75, pb: 0.45, maxHeight: 248, overflowY: 'auto', ...hideScrollbarSx }}>
            {mentionOptions.map((member, index) => {
              const selected = index === mentionActive;
              return (
                <Stack
                  key={member.key}
                  direction="row"
                  spacing={1.15}
                  alignItems="center"
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setMentionCursor(index)}
                  onClick={() => insertMention(member.name)}
                  sx={{
                    px: 1,
                    py: 0.8,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: selected ? (tokens.isDark ? 'rgba(124, 108, 246, 0.22)' : '#F1EEFF') : 'transparent',
                  }}
                >
                  {member.everyone ? (
                    <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C6CF6', flexShrink: 0 }}>
                      <PeopleAltOutlinedIcon sx={{ fontSize: 22 }} />
                    </Box>
                  ) : (
                    <Initials name={member.name} color={member.color} size={36} tokens={tokens} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <Typography noWrap sx={{ fontSize: tokens.fontSize - 2, fontWeight: 400, color: tokens.primary, fontFamily: tokens.fontFamily, lineHeight: 1.2, textAlign: 'left' }}>
                      {member.name}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: tokens.fontSize - 5, fontWeight: 400, color: tokens.isDark ? tokens.secondary : '#8B95A8', fontFamily: tokens.fontFamily, lineHeight: 1.25, textAlign: 'left' }}>
                      {member.subtitle}
                    </Typography>
                  </Box>
                  {selected && <CheckIcon sx={{ fontSize: 18, color: tokens.isDark ? '#E2E8F0' : '#243044', flexShrink: 0 }} />}
                </Stack>
              );
            })}
          </Box>
          <Stack
            direction="row"
            spacing={1.75}
            alignItems="center"
            sx={{ px: 1.75, py: 1, borderTop: `1px solid ${tokens.isDark ? tokens.border : '#EEF1F4'}`, flexWrap: 'wrap' }}
          >
            {['↑ ↓ to choose', 'Enter to mention', 'Esc to close'].map((hint) => (
              <Typography key={hint} sx={{ fontSize: 11.5, fontWeight: 400, color: mentionHint, fontFamily: tokens.fontFamily, whiteSpace: 'nowrap' }}>
                {hint}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
      {withEmoji && (
        <SentimentSatisfiedAltOutlinedIcon sx={{ position: 'absolute', left: 14, zIndex: 1, color: tokens.secondary, fontSize: 18, pointerEvents: 'none' }} />
      )}
      <Box
        component="input"
        value={draft}
        placeholder="Message"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onMentionKeyDown}
        sx={{
          flex: 1,
          minWidth: 0,
          height: 42,
          pl: withEmoji ? 4.6 : 1.75,
          pr: 1.75,
          borderRadius: '22px',
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.isDark ? '#121a2b' : tokens.surface,
          color: tokens.primary,
          fontSize: tokens.fontSize - 0.5,
          fontFamily: tokens.fontFamily,
          outline: 'none',
          '&::placeholder': { color: tokens.secondary, opacity: 1 },
        }}
      />
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: showSend ? (hasText ? tokens.brand : tokens.secondary) : 'transparent',
          color: showSend ? '#ffffff' : tokens.primary,
          ...(!showSend && {
            cursor: 'pointer',
            '&:hover': {
              color: tokens.brand,
              backgroundColor: withAlpha(tokens.brand, '16'),
            },
          }),
        }}
      >
        {showSend ? (
          <FontAwesomeIcon icon={faPaperPlaneTop} style={{ fontSize: 16 }} />
        ) : (
          <MicNoneIcon sx={{ fontSize: 22 }} />
        )}
      </Box>
    </Box>
  );
};

const EditComposerBar: React.FC<{
  tokens: ThemeTokens;
  preview: string;
  onClose: () => void;
}> = ({ tokens, preview, onClose }) => (
  <Stack direction="row" spacing={1.1} alignItems="flex-start" justifyContent="flex-start" sx={{ px: 1.75, pt: 1.15, textAlign: 'left' }}>
    <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: '3px', backgroundColor: tokens.brand, flexShrink: 0 }} />
    <EditOutlinedIcon sx={{ fontSize: 16, color: tokens.brand, mt: '1px' }} />
    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
      <Typography sx={{ fontWeight: 700, fontSize: tokens.fontSize - 0.5, color: tokens.primary, fontFamily: tokens.fontFamily, lineHeight: 1.25, textAlign: 'left' }}>
        Edit message
      </Typography>
      <Typography noWrap sx={{ fontSize: tokens.fontSize - 2, color: tokens.secondary, fontFamily: tokens.fontFamily, textAlign: 'left' }}>
        {preview}
      </Typography>
    </Box>
    <Box onClick={onClose} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}>
      <CloseIcon sx={{ fontSize: 16 }} />
    </Box>
  </Stack>
);

const ReplyComposerBar: React.FC<{
  tokens: ThemeTokens;
  name: string;
  preview: string;
  onClose: () => void;
}> = ({ tokens, name, preview, onClose }) => (
  <Stack direction="row" spacing={1.1} alignItems="flex-start" justifyContent="flex-start" sx={{ px: 1.75, pt: 1.15, textAlign: 'left' }}>
    <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: '3px', backgroundColor: tokens.brand, flexShrink: 0 }} />
    <ReplyOutlinedIcon sx={{ fontSize: 16, color: tokens.brand, mt: '1px' }} />
    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
      <Typography sx={{ fontWeight: 700, fontSize: tokens.fontSize - 0.5, color: tokens.primary, fontFamily: tokens.fontFamily, lineHeight: 1.25, textAlign: 'left' }}>
        Replying to {name}
      </Typography>
      <Typography noWrap sx={{ fontSize: tokens.fontSize - 2, color: tokens.secondary, fontFamily: tokens.fontFamily, textAlign: 'left' }}>
        {preview}
      </Typography>
    </Box>
    <Box onClick={onClose} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}>
      <CloseIcon sx={{ fontSize: 16 }} />
    </Box>
  </Stack>
);

const ThreadPanel: React.FC<{
  tokens: ThemeTokens;
  name: string;
  text: string;
  time: string;
  avatarColor?: string;
  overlay?: boolean;
  onClose: () => void;
}> = ({ tokens, name, text, time, avatarColor = '#DDD6FE', overlay, onClose }) => {
  const [draft, setDraft] = useState('');
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const mute = tokens.isDark ? tokens.secondary : '#94A3B8';
  const ink = tokens.isDark ? tokens.primary : '#1E293B';
  const hairline = tokens.isDark ? tokens.border : '#E6EBF2';

  const resizeField = () => {
    const field = fieldRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, 120)}px`;
  };

  const sendDraft = () => {
    if (!draft.trim()) return;
    setDraft('');
    if (fieldRef.current) fieldRef.current.style.height = 'auto';
  };

  return (
    <Box
      sx={{
        width: overlay ? '100%' : 360,
        flexShrink: 0,
        height: '100%',
        borderLeft: overlay ? 'none' : `1px solid ${hairline}`,
        backgroundColor: tokens.surface,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minHeight: 0,
        textAlign: 'left',
        fontFamily: tokens.fontFamily,
        ...(overlay && {
          position: 'absolute',
          inset: 0,
          zIndex: 5,
        }),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.25, pt: 2.1, pb: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 11.5,
            letterSpacing: '0.12em',
            color: tokens.isDark ? tokens.secondary : '#5C6B82',
            fontFamily: tokens.fontFamily,
            lineHeight: 1.2,
          }}
        >
          MESSAGE THREAD
        </Typography>
        <Box
          onClick={onClose}
          sx={{
            cursor: 'pointer',
            color: mute,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '8px',
            '&:hover': { backgroundColor: tokens.isDark ? '#1F2937' : '#F3F4F6', color: ink },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </Box>
      </Stack>

      <Typography sx={{ px: 2.25, pt: 1.6, pb: 1.5, fontSize: 13.5, lineHeight: 1.4, color: mute, fontFamily: tokens.fontFamily }}>
        Replies to this message
        <Box component="span" sx={{ mx: 0.7 }}>·</Box>
        <Box component="span" sx={{ color: ink, fontWeight: 600 }}>{name}</Box>
      </Typography>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.25, display: 'flex', flexDirection: 'column', ...hideScrollbarSx }}>
        <Box
          sx={{
            px: 1.6,
            py: 1.45,
            borderRadius: '16px',
            backgroundColor: tokens.isDark ? '#141C2E' : '#F6F7FB',
            border: `1px solid ${hairline}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.15}>
            <AvatarBubble name={name} color={avatarColor} size={36} ink={ink} fontFamily={tokens.fontFamily} />
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, color: ink, fontFamily: tokens.fontFamily }}>
                {name}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: 12, lineHeight: 1.3, color: mute, fontFamily: tokens.fontFamily }}>
                {time}
              </Typography>
            </Box>
            <ArrowForwardIcon sx={{ fontSize: 18, color: mute, flexShrink: 0 }} />
          </Stack>
          <Typography sx={{ mt: 1.45, fontSize: 14.5, lineHeight: 1.45, color: ink, fontFamily: tokens.fontFamily, textAlign: 'left' }}>
            {text}
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 2, mb: 1 }}>
          <Typography sx={{ fontSize: 13, color: mute, fontFamily: tokens.fontFamily, flexShrink: 0 }}>0 replies</Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: hairline }} />
        </Stack>

        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, textAlign: 'center', px: 2, py: 3 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '14px',
              backgroundColor: tokens.isDark ? withAlpha(tokens.brand, '22') : '#F3EEFC',
              color: tokens.isDark ? tokens.brand : '#7C6AD6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ mt: 1.7, fontWeight: 700, fontSize: 16, lineHeight: 1.3, color: ink, fontFamily: tokens.fontFamily }}>
            Start the conversation
          </Typography>
          <Typography sx={{ mt: 0.55, fontSize: 13.5, lineHeight: 1.45, color: mute, fontFamily: tokens.fontFamily }}>
            Replies stay together in this thread.
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ px: 2, pt: 0.5, pb: 1.6 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 64,
            pl: 1.75,
            pr: 0.85,
            py: 0.75,
            borderRadius: '16px',
            border: `1px solid ${hairline}`,
            backgroundColor: tokens.surface,
          }}
        >
          <Box
            component="textarea"
            ref={fieldRef}
            value={draft}
            rows={1}
            placeholder="Reply in thread..."
            onChange={(event) => {
              setDraft(event.target.value);
              resizeField();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                sendDraft();
              }
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'block',
              border: 'none',
              outline: 'none',
              resize: 'none',
              background: 'transparent',
              color: ink,
              fontSize: 14.5,
              lineHeight: 1.4,
              fontFamily: tokens.fontFamily,
              maxHeight: 120,
              m: 0,
              p: 0,
              overflow: 'hidden',
              '&::placeholder': { color: tokens.isDark ? tokens.secondary : '#B4BCC8', opacity: 1 },
            }}
          />
          <Box
            onClick={sendDraft}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
              backgroundColor: tokens.isDark ? tokens.brand : '#C4B5FD',
              color: '#ffffff',
            }}
          >
            <FontAwesomeIcon icon={faPaperPlaneTop} style={{ fontSize: 13 }} />
          </Box>
        </Box>
        <Typography sx={{ mt: 0.85, px: 0.4, fontSize: 11.5, lineHeight: 1.3, color: mute, fontFamily: tokens.fontFamily }}>
          Enter to send · Shift + Enter for a new line
        </Typography>
      </Box>
    </Box>
  );
};

type GroupSheet = 'manage' | 'member' | 'scope' | 'add' | 'ban' | 'transfer';

const GroupsScreen: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  compact?: boolean;
  onNavigate: (id: KitScreenId, targetId?: string) => void;
  onOpenModal: (id: KitModalId) => void;
}> = ({ tokens, config, compact, onNavigate, onOpenModal }) => {
  const rows = GROUPS.filter((group) => {
    if (group.type === 'public') return config.chat_feature.is_public_group;
    return config.chat_feature.is_private_and_protected_group;
  });
  const [selectedId, setSelectedId] = useState(rows.find((group) => group.id === 'h2')?.id || rows[0]?.id || '');
  const [showChat, setShowChat] = useState(!compact);
  const [detailsOpen, setDetailsOpen] = useState(!compact);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<{ name: string; text: string } | null>(null);
  const [threadMessage, setThreadMessage] = useState<{ name: string; text: string; time: string } | null>(null);
  const [stack, setStack] = useState<GroupSheet[]>([]);
  const [scope, setScope] = useState<'Participant' | 'Admin' | 'Moderator'>('Participant');
  const [picked, setPicked] = useState(false);
  const [banReason, setBanReason] = useState('');
  const selected = rows.find((group) => group.id === selectedId) || rows[0];
  const chatVisible = Boolean(selected) && (compact ? showChat : true);
  const showVoice = config.call_feature.is_group_voice_calls;
  const showVideo = config.call_feature.is_group_video_calls;
  const canCreate = config.chat_feature.is_public_group || config.chat_feature.is_private_and_protected_group;
  const pushSheet = (id: GroupSheet) => setStack((current) => [...current, id]);
  const popSheet = () => setStack((current) => current.slice(0, -1));
  const iconBtn = {
    width: 32,
    height: 32,
    borderRadius: '10px',
    border: `1.5px solid ${withAlpha(tokens.brand, '35')}`,
    color: tokens.brand,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  } as const;

  const hoverMenu = (id: string, preview: { name: string; text: string; time: string }) => (
    <Box className="bubble-tools" sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.45, flexShrink: 0 }}>
      {config.chat_feature.is_message_reaction && (
        <Box
          onClick={(event) => {
            event.stopPropagation();
            setMenuId(id);
          }}
          sx={{ width: 28, height: 28, borderRadius: '8px', border: `1px solid ${tokens.border}`, backgroundColor: tokens.surface, color: tokens.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <SentimentSatisfiedAltOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      <Box
        onClick={(event) => {
          event.stopPropagation();
          setMenuId((current) => (current === id ? null : id));
        }}
        sx={{ width: 28, height: 28, borderRadius: '8px', border: `1px solid ${tokens.border}`, backgroundColor: tokens.surface, color: tokens.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      >
        <MoreHorizIcon sx={{ fontSize: 16 }} />
      </Box>
      {menuId === id && (config.chat_feature.is_message_reaction || config.chat_feature.is_quote_reply || config.chat_feature.is_thread || config.chat_feature.is_delete_chat) && (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: 'absolute',
            left: 0,
            top: 'calc(100% + 6px)',
            zIndex: 4,
            py: 0.8,
            px: 0.5,
            minWidth: 168,
            borderRadius: '14px',
            backgroundColor: tokens.isDark ? '#141c2e' : tokens.surface,
            border: `1px solid ${tokens.border}`,
            boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
          }}
        >
          {config.chat_feature.is_message_reaction && (
            <Stack direction="row" justifyContent="space-around" sx={{ px: 0.6, pb: 0.7, mb: 0.4 }}>
              {['👍', '❤️', '😂', '🎉'].map((emoji) => (
                <Typography key={emoji} sx={{ fontSize: 16, lineHeight: 1, cursor: 'pointer' }}>{emoji}</Typography>
              ))}
            </Stack>
          )}
          {[
            config.chat_feature.is_quote_reply ? { label: 'Reply', icon: <ReplyOutlinedIcon sx={{ fontSize: 16 }} />, id: 'reply' as const } : null,
            config.chat_feature.is_thread ? { label: 'Thread', icon: <ForumOutlinedIcon sx={{ fontSize: 16 }} />, id: 'thread' as const } : null,
            config.chat_feature.is_delete_chat ? { label: 'Delete', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />, id: 'delete' as const } : null,
          ]
            .filter((item): item is { label: string; icon: React.ReactElement; id: 'reply' | 'thread' | 'delete' } => Boolean(item))
            .map((item) => (
              <Stack
                key={item.label}
                direction="row"
                spacing={1.1}
                alignItems="center"
                onClick={() => {
                  setMenuId(null);
                  if (item.id === 'reply') setReplyTo(preview);
                  if (item.id === 'thread') setThreadMessage(preview);
                  if (item.id === 'delete') onOpenModal('delete-message');
                }}
                sx={{ px: 1.1, py: 0.7, cursor: 'pointer', color: item.id === 'delete' ? '#f05252' : tokens.primary, borderRadius: '8px', '&:hover': { backgroundColor: withAlpha(tokens.brand, '12') } }}
              >
                {item.icon}
                <Typography sx={{ fontSize: tokens.fontSize - 1, fontFamily: tokens.fontFamily, fontWeight: 500, color: 'inherit' }}>{item.label}</Typography>
              </Stack>
            ))}
        </Box>
      )}
    </Box>
  );

  const sheetCard = (children: React.ReactNode, width = 440) => (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        width: '100%',
        maxWidth: width,
        backgroundColor: tokens.isDark ? '#161e30' : tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: '18px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        p: 2.25,
        fontFamily: tokens.fontFamily,
      }}
    >
      {children}
    </Box>
  );

  const actionBtn = (label: string, icon: React.ReactNode, onClick: () => void, danger = false) => (
    <Stack
      direction="row"
      spacing={0.8}
      alignItems="center"
      justifyContent="center"
      onClick={onClick}
      sx={{
        flex: 1,
        minHeight: 44,
        borderRadius: '12px',
        border: `1px solid ${tokens.border}`,
        cursor: 'pointer',
        color: danger ? '#f05252' : tokens.primary,
        '&:hover': { backgroundColor: withAlpha(tokens.brand, '10') },
      }}
    >
      {icon}
      <Typography sx={{ ...textSx(tokens, -1, 600), color: 'inherit' }}>{label}</Typography>
    </Stack>
  );

  return (
    <Box sx={{ height: '100%', minWidth: 0, display: 'flex', position: 'relative' }}>
      <Box
        sx={{
          width: chatVisible && !compact ? { xs: 268, lg: 300 } : '100%',
          minWidth: chatVisible && !compact ? { xs: 248, lg: 280 } : 0,
          display: chatVisible && compact ? 'none' : 'flex',
          height: '100%',
          flexDirection: 'column',
          borderRight: `1px solid ${tokens.border}`,
          backgroundColor: tokens.surface,
          flexShrink: 0,
        }}
      >
        <Box sx={{ px: 2.25, pt: 2.25, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.75 }}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ ...labelSx(tokens), mb: 0.45 }}>Communications</Typography>
              <Typography sx={{ ...textSx(tokens, 6, 700), lineHeight: 1.1 }}>Groups</Typography>
            </Box>
            <Stack direction="row" spacing={0.8}>
              {config.call_feature.is_schedule_calls && (
                <Box onClick={() => onNavigate('schedule-form')} sx={iconBtn}>
                  <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
                </Box>
              )}
              {canCreate && (
                <Box onClick={() => onOpenModal('create-group')} sx={iconBtn}>
                  <AddIcon sx={{ fontSize: 18 }} />
                </Box>
              )}
            </Stack>
          </Stack>
          {config.chat_feature.is_search_message && (
            <Box sx={{ height: 40, px: 1.5, borderRadius: '8px', backgroundColor: tokens.isDark ? '#1F2937' : '#F3F4F6', border: `1px solid ${tokens.border}`, display: 'flex', alignItems: 'center', gap: 1, color: tokens.muted }}>
              <SearchOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.muted, fontFamily: tokens.fontFamily }}>Search chats</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ borderTop: `1px solid ${tokens.border}` }} />
        <Typography sx={{ ...labelSx(tokens), px: 2.25, pt: 1.5, pb: 0.75 }}>Your groups</Typography>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1.5, ...hideScrollbarSx }}>
          <Stack spacing={0.45}>
            {rows.map((group) => {
              const active = group.id === selected?.id;
              return (
                <Box
                  key={group.id}
                  onClick={() => {
                    setSelectedId(group.id);
                    setShowChat(true);
                    setMenuId(null);
                  }}
                  sx={{
                    ...selectedRowSx(tokens, active),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.1,
                    py: 1,
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ position: 'relative', width: 42, height: 42, flexShrink: 0 }}>
                    <Box component="img" src={group.photo} alt="" sx={{ width: 42, height: 42, borderRadius: '12px', objectFit: 'cover', bgcolor: group.color, display: 'block' }} />
                    {group.unread > 0 && (
                      <Box sx={{ position: 'absolute', right: -5, bottom: -4, minWidth: 18, height: 18, px: 0.4, borderRadius: '9px', backgroundColor: '#e11d48', color: '#fff', border: `2px solid ${tokens.surface}`, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {group.unread}
                      </Box>
                    )}
                  </Box>
                  <Typography noWrap sx={{ ...textSx(tokens, -0.5, 700) }}>{group.name}</Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
        <ProfileFooter tokens={tokens} showPresence={config.chat_feature.is_user_presence} />
      </Box>

      {selected && chatVisible && (
        <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', backgroundColor: tokens.surface }}>
        <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1.15, borderBottom: `1px solid ${tokens.border}`, gap: 1 }}>
            <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
              {compact && (
                <Box onClick={() => setShowChat(false)} sx={{ cursor: 'pointer', color: tokens.primary, display: 'flex' }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                </Box>
              )}
              <Box sx={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#d9f99d', color: '#3f342b' }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 22 }} />
                </Avatar>
                {config.chat_feature.is_user_presence && (
                  <Box sx={{ position: 'absolute', right: 0, bottom: 0, width: 9, height: 9, borderRadius: '50%', backgroundColor: '#22C55E', border: `2px solid ${tokens.surface}` }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography noWrap sx={{ ...textSx(tokens, 0, 700) }}>{selected.name}</Typography>
                <Stack direction="row" spacing={0.45} alignItems="center">
                  {config.chat_feature.is_user_presence && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22C55E', flexShrink: 0 }} />}
                  <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>2 participants · {selected.type} group</Typography>
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.2} alignItems="center">
              {showVoice && <Box onClick={() => onOpenModal('outgoing')} sx={headerIconSx(tokens)}><CallOutlinedIcon sx={{ fontSize: 20 }} /></Box>}
              {showVideo && <Box onClick={() => onOpenModal('outgoing')} sx={headerIconSx(tokens)}><VideocamOutlinedIcon sx={{ fontSize: 20 }} /></Box>}
              {config.chat_feature.is_search_message && <Box onClick={() => onOpenModal('search')} sx={headerIconSx(tokens)}><SearchOutlinedIcon sx={{ fontSize: 20 }} /></Box>}
              <Box onClick={() => pushSheet('manage')} sx={headerIconSx(tokens)}><PeopleOutlineIcon sx={{ fontSize: 20 }} /></Box>
              <Box onClick={() => setDetailsOpen((open) => !open)} sx={headerIconSx(tokens)}><InfoOutlinedIcon sx={{ fontSize: 20 }} /></Box>
            </Stack>
          </Stack>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.15, backgroundColor: tokens.isDark ? tokens.surface : '#FAFBFC', ...hideScrollbarSx }}>
            {[
              { id: 'in-1', text: 'jhbasbkd', time: '10:35 AM', quote: null as null | { name: string; text: string }, reaction: true },
              { id: 'in-2', text: 'hfahda', time: '10:36 AM', quote: { name: 'Ravi', text: 'hello' }, reaction: false },
            ].map((message) => (
              <Box key={message.id} onMouseLeave={() => setMenuId(null)} sx={{ alignSelf: 'flex-start', maxWidth: '78%' }}>
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mb: 0.4 }}>
                  <Box sx={{ position: 'relative', width: 22, height: 22 }}>
                    <Avatar sx={{ width: 22, height: 22, bgcolor: '#7C3AED', color: '#fff', fontSize: 11, fontWeight: 700 }}>R</Avatar>
                    {config.chat_feature.is_user_presence && (
                      <Box sx={{ position: 'absolute', right: -1, bottom: -1, width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22C55E', border: `1.5px solid ${tokens.surface}` }} />
                    )}
                  </Box>
                  <Typography sx={{ ...textSx(tokens, -1.5, 700), color: tokens.brand }}>Ravi</Typography>
                </Stack>
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <Box sx={{ px: message.quote ? 1.15 : 1.5, py: message.quote ? 0.75 : 0.7, borderRadius: '14px 14px 14px 3px', backgroundColor: tokens.isDark ? '#1a2438' : '#F3F4F6', maxWidth: 280 }}>
                    {message.quote && (
                      <Box sx={{ mb: 0.55, px: 0.9, py: 0.45, borderRadius: '8px', borderLeft: `3px solid ${tokens.brand}`, backgroundColor: tokens.isDark ? '#121a2b' : '#ECEEF3' }}>
                        <Typography sx={{ fontSize: tokens.fontSize - 2, fontWeight: 700, color: tokens.brand, fontFamily: tokens.fontFamily, lineHeight: 1.2 }}>{message.quote.name}</Typography>
                        <Typography sx={{ fontSize: tokens.fontSize - 2, color: tokens.secondary, fontFamily: tokens.fontFamily, lineHeight: 1.3 }}>{message.quote.text}</Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={0.8} alignItems="flex-end">
                      <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.primary, fontFamily: tokens.fontFamily }}>{message.text}</Typography>
                      <Typography sx={{ fontSize: tokens.fontSize - 3, color: tokens.secondary, fontFamily: tokens.fontFamily, whiteSpace: 'nowrap' }}>{message.time}</Typography>
                    </Stack>
                  </Box>
                  {hoverMenu(message.id, { name: 'Ravi', text: message.text, time: message.time })}
                </Stack>
                {message.reaction && config.chat_feature.is_message_reaction && (
                  <Box sx={{ mt: 0.45, ml: 3.4, width: 'fit-content', px: 0.7, py: 0.1, borderRadius: '99px', border: '1px solid #FDBA74', backgroundColor: tokens.surface, display: 'flex', alignItems: 'center', gap: 0.35 }}>
                    <Typography sx={{ fontSize: 12, lineHeight: 1.2 }}>😮</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: tokens.secondary, fontFamily: tokens.fontFamily }}>1</Typography>
                  </Box>
                )}
              </Box>
            ))}

            <Stack spacing={0.7} sx={{ alignSelf: 'flex-end', alignItems: 'flex-end', maxWidth: '78%' }}>
              {[
                { id: 'out-1', text: 'bvgascdgh ad', time: '10:58 AM' },
                { id: 'out-2', text: 'hsdgfadsgvsds', time: '10:58 AM' },
                { id: 'out-3', text: 'jhbashd asd', time: '11:00 AM' },
                { id: 'out-4', text: 'hasdghasvd', time: '11:01 AM' },
              ].map((message) => (
                <Box key={message.id} onMouseLeave={() => setMenuId(null)} sx={{ px: 1.5, py: 0.65, borderRadius: '14px 14px 3px 14px', backgroundColor: tokens.brand, maxWidth: '100%' }}>
                  <Stack direction="row" spacing={0.7} alignItems="flex-end">
                    <Typography sx={{ fontSize: tokens.fontSize - 0.5, fontWeight: 600, color: '#fff', fontFamily: tokens.fontFamily }}>{message.text}</Typography>
                    <Stack direction="row" spacing={0.3} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Typography sx={{ fontSize: tokens.fontSize - 3, color: 'rgba(255,255,255,0.85)', fontFamily: tokens.fontFamily, whiteSpace: 'nowrap' }}>{message.time}</Typography>
                      {config.chat_feature.is_read_and_delivered_receipts && <DoneAllIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }} />}
                    </Stack>
                  </Stack>
                </Box>
              ))}
              <Box onMouseLeave={() => setMenuId(null)} sx={{ px: 1.35, py: 0.75, borderRadius: '14px 14px 3px 14px', backgroundColor: tokens.brand, maxWidth: '100%' }}>
                <Box sx={{ mb: 0.55, px: 0.9, py: 0.45, borderRadius: '8px', borderLeft: '3px solid rgba(255,255,255,0.75)', backgroundColor: 'rgba(255,255,255,0.16)' }}>
                  <Typography sx={{ fontSize: tokens.fontSize - 2, fontWeight: 700, color: '#fff', fontFamily: tokens.fontFamily, lineHeight: 1.2 }}>Ravi</Typography>
                  <Typography sx={{ fontSize: tokens.fontSize - 2, color: 'rgba(255,255,255,0.8)', fontFamily: tokens.fontFamily, lineHeight: 1.3 }}>hfahda</Typography>
                </Box>
                <Stack direction="row" spacing={0.7} alignItems="flex-end">
                  <Typography sx={{ fontSize: tokens.fontSize - 0.5, fontWeight: 600, color: '#fff', fontFamily: tokens.fontFamily }}>hjvasghvda</Typography>
                  <Stack direction="row" spacing={0.3} alignItems="center" sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontSize: tokens.fontSize - 3, color: 'rgba(255,255,255,0.85)', fontFamily: tokens.fontFamily, whiteSpace: 'nowrap' }}>11:01 AM</Typography>
                    {config.chat_feature.is_read_and_delivered_receipts && <DoneAllIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }} />}
                  </Stack>
                </Stack>
              </Box>
            </Stack>

            <Box sx={{ alignSelf: 'center', mt: 1.2, px: 1.1, py: 0.2, borderRadius: '99px', backgroundColor: tokens.isDark ? '#1a2438' : '#EEF1F6' }}>
              <Typography sx={{ ...textSx(tokens, -2.2, 600), color: tokens.secondary }}>Yesterday</Typography>
            </Box>
            <Typography sx={{ alignSelf: 'center', ...textSx(tokens, -2, 500), color: tokens.secondary, textAlign: 'center' }}>
              Ajit removed Ravi from the group
            </Typography>
          </Box>

          <Box sx={{ borderTop: `1px solid ${tokens.border}` }}>
            {replyTo && (
              <ReplyComposerBar tokens={tokens} name={replyTo.name} preview={replyTo.text} onClose={() => setReplyTo(null)} />
            )}
            <Box sx={{ px: 1.5, py: 1.15, display: 'flex', alignItems: 'center', gap: 1 }}>
              {config.chat_feature.is_media_file_sharing && (
                <Box sx={composerSideIconSx(tokens)}>
                  <AddIcon sx={{ fontSize: 18 }} />
                </Box>
              )}
              <MessageComposerField
                tokens={tokens}
                voiceEnabled={config.chat_feature.is_voice_message}
                mentionsEnabled={config.chat_feature.is_mentions}
                mentions={CREATE_GROUP_PEOPLE}
                withEmoji
              />
            </Box>
          </Box>
        </Box>
        {threadMessage && (
          <ThreadPanel
            tokens={tokens}
            name={threadMessage.name}
            text={threadMessage.text}
            time={threadMessage.time}
            overlay={compact}
            onClose={() => setThreadMessage(null)}
          />
        )}
        </Box>
      )}

      {selected && detailsOpen && chatVisible && !compact && !threadMessage && (
        <Box sx={{ width: 280, flexShrink: 0, borderLeft: `1px solid ${tokens.border}`, backgroundColor: tokens.surface, overflowY: 'auto', px: 2, py: 1.75, textAlign: 'left', ...hideScrollbarSx }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: tokens.secondary }}>CONVERSATION DETAILS</Typography>
            <Box onClick={() => setDetailsOpen(false)} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}><CloseIcon sx={{ fontSize: 16 }} /></Box>
          </Stack>
          <Stack alignItems="center" sx={{ pt: 2.5, pb: 1.6, textAlign: 'center' }}>
            <Avatar sx={{ width: 72, height: 72, bgcolor: '#d9f99d', color: tokens.brand }}><GroupsOutlinedIcon sx={{ fontSize: 34 }} /></Avatar>
            <Typography sx={{ mt: 1.1, ...textSx(tokens, 2, 700) }}>{selected.name}</Typography>
            <Typography sx={{ ...textSx(tokens, -1.5, 500), color: tokens.secondary, mt: 0.3 }}>2 members · {selected.type} group</Typography>
            {config.chat_feature.is_user_presence && (
              <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mt: 0.9, px: 1.05, py: 0.3, borderRadius: '99px', backgroundColor: tokens.isDark ? 'rgba(34,197,94,0.16)' : '#E7F8EE' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#22C55E' }} />
                <Typography sx={{ ...textSx(tokens, -2, 600), color: '#16A34A' }}>Online now</Typography>
              </Stack>
            )}
          </Stack>
          <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />
          <Typography sx={{ ...textSx(tokens, -0.5, 700), mb: 1 }}>Quick actions</Typography>
          {(() => {
            const actions = [
              { label: 'Chat', icon: <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />, onClick: undefined as undefined | (() => void) },
              ...(showVoice ? [{ label: 'Voice', icon: <CallOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') }] : []),
              ...(showVideo ? [{ label: 'Video', icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') }] : []),
              ...(config.chat_feature.is_search_message ? [{ label: 'Search', icon: <SearchOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('search') }] : []),
            ];
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${actions.length}, 1fr)`, gap: 0.7, mb: 1.6 }}>
                {actions.map((action) => (
                  <Box
                    key={action.label}
                    onClick={action.onClick}
                    sx={{
                      border: `1px solid ${tokens.border}`,
                      borderRadius: '12px',
                      py: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      color: tokens.secondary,
                      backgroundColor: tokens.surface,
                      '&:hover': {
                        color: tokens.brand,
                        borderColor: tokens.brand,
                        backgroundColor: withAlpha(tokens.brand, '12'),
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.35, color: 'inherit' }}>{action.icon}</Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: tokens.fontFamily, color: 'inherit' }}>{action.label}</Typography>
                  </Box>
                ))}
              </Box>
            );
          })()}
          <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />
          {config.chat_feature.is_media_file_sharing && (
            <Box sx={{ mb: 1.6 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.1 }}>
                <Typography sx={{ ...textSx(tokens, -0.5, 700) }}>Shared files</Typography>
                <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>08</Typography>
              </Stack>
              {[
                ['images (3).jpg', 'Sep 16 · 30 KB'],
                ['images (4).jpg', 'Sep 16 · 53 KB'],
              ].map(([name, meta]) => (
                <Stack key={name} direction="row" spacing={1} alignItems="center" sx={{ mb: 1.05 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', backgroundColor: tokens.isDark ? '#1F2937' : withAlpha(tokens.brand, '10'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <ImageOutlinedIcon sx={{ fontSize: 18, color: tokens.brand }} />
                  </Box>
                  <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                    <Typography noWrap sx={{ ...textSx(tokens, -1.5, 700) }}>{name}</Typography>
                    <Typography sx={{ ...textSx(tokens, -2.5, 500), color: tokens.secondary }}>{meta}</Typography>
                  </Box>
                </Stack>
              ))}
              <Typography sx={{ ...textSx(tokens, -1.5, 600), color: tokens.brand, textAlign: 'left', display: 'block', mt: 0.4 }}>View all files →</Typography>
            </Box>
          )}
          {config.chat_feature.is_media_file_sharing && <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0.6, textAlign: 'left' }}>
            <Typography sx={{ ...textSx(tokens, -0.5, 700), textAlign: 'left', maxWidth: '46%', lineHeight: 1.25 }}>Group management</Typography>
            <Typography sx={{ ...textSx(tokens, -2.2, 600), color: tokens.brand, textAlign: 'left', maxWidth: '48%', lineHeight: 1.3 }}>2 members · {selected.type} group</Typography>
          </Stack>
          <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary, mb: 1.2, lineHeight: 1.45, textAlign: 'left' }}>
            Add or remove participants, update roles, manage access, or transfer ownership.
          </Typography>
          <Stack direction="row" spacing={0.7} alignItems="center" onClick={() => pushSheet('manage')} sx={{ cursor: 'pointer', color: tokens.brand, width: 'fit-content', mb: 1.6 }}>
            <PeopleOutlineIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ ...textSx(tokens, -1, 700), color: tokens.brand }}>Manage group →</Typography>
          </Stack>
          {config.chat_feature.is_delete_conversation && (
            <>
              <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />
              <Typography sx={{ ...textSx(tokens, -0.5, 700), mb: 1, textAlign: 'left' }}>Privacy</Typography>
              <Stack direction="row" spacing={0.8} alignItems="center" onClick={() => onOpenModal('delete-conversation')} sx={{ cursor: 'pointer', color: '#e5484d', width: 'fit-content', mb: 1.6 }}>
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ ...textSx(tokens, -1, 600), color: 'inherit' }}>Delete conversation</Typography>
              </Stack>
            </>
          )}
          <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />
          <Typography sx={{ ...textSx(tokens, -0.5, 700), mb: 0.6, textAlign: 'left' }}>About this conversation</Typography>
          <Typography sx={{ ...textSx(tokens, -1.5, 500), color: tokens.secondary, lineHeight: 1.45, textAlign: 'left' }}>
            Keep messages, files, voice, and video together in a single place.
          </Typography>
        </Box>
      )}

      {stack.length > 0 && (
        <Box onClick={popSheet} sx={{ position: 'absolute', inset: 0, zIndex: 30, backgroundColor: 'rgba(2, 6, 23, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {stack.map((sheet, index) => (
            <Box key={`${sheet}-${index}`} sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 31 + index, width: sheet === 'manage' ? 460 : 400, maxWidth: 'calc(100% - 24px)' }}>
              {sheet === 'manage' && sheetCard(
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ ...labelSx(tokens), color: tokens.brand }}>Group</Typography>
                      <Typography sx={{ ...textSx(tokens, 4, 700), mt: 0.3 }}>Manage {selected?.name}</Typography>
                    </Box>
                    <Box onClick={popSheet} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}><CloseIcon sx={{ fontSize: 18 }} /></Box>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.6, pb: 1.6, borderBottom: `1px solid ${tokens.border}` }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar sx={{ width: 44, height: 44, bgcolor: '#d9f99d', color: '#3f342b' }}><GroupsOutlinedIcon sx={{ fontSize: 22 }} /></Avatar>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography sx={{ ...textSx(tokens, 0, 700) }}>{selected?.name}</Typography>
                        <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>2 members · public group</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.6} alignItems="center" onClick={() => pushSheet('add')} sx={{ px: 1.3, py: 0.7, borderRadius: '12px', border: `1px solid ${tokens.border}`, cursor: 'pointer' }}>
                      <PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography sx={{ ...textSx(tokens, -1.5, 600) }}>Add people</Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.4, mb: 0.8 }}>
                    <Typography sx={labelSx(tokens)}>Participants</Typography>
                    <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>2 members</Typography>
                  </Stack>
                  <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 1.4, py: 1.15, borderBottom: `1px solid ${tokens.border}` }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 14 }}>A</Avatar>
                      <Box sx={{ flex: 1, textAlign: 'left' }}>
                        <Typography sx={{ ...textSx(tokens, -0.5, 700) }}>You</Typography>
                        <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>Owner</Typography>
                      </Box>
                      <Typography sx={{ ...labelSx(tokens), color: tokens.brand }}>Owner</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 1.4, py: 1.15 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 14 }}>R</Avatar>
                      <Box sx={{ flex: 1, textAlign: 'left' }}>
                        <Typography sx={{ ...textSx(tokens, -0.5, 700) }}>Ravi</Typography>
                        <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>
                          {config.chat_feature.is_user_presence ? 'Member · Offline' : 'Member'}
                        </Typography>
                      </Box>
                      <Box onClick={() => pushSheet('member')} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}>
                        <MoreHorizIcon sx={{ fontSize: 18 }} />
                      </Box>
                    </Stack>
                  </Box>
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    <Stack direction="row" spacing={1}>
                      {actionBtn('Mute group', <NotificationsNoneIcon sx={{ fontSize: 16 }} />, () => undefined)}
                      {actionBtn('Transfer ownership', <PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />, () => pushSheet('transfer'))}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {actionBtn('Leave group', <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />, () => onOpenModal('confirm'))}
                      {config.chat_feature.is_delete_conversation && actionBtn('Delete group', <DeleteOutlineIcon sx={{ fontSize: 16 }} />, () => onOpenModal('confirm'), true)}
                    </Stack>
                  </Stack>
                </>
              )}
              {sheet === 'member' && sheetCard(
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ ...textSx(tokens, -1.5, 700), color: tokens.brand }}>{selected?.name}</Typography>
                      <Typography sx={{ ...textSx(tokens, 4, 700) }}>Manage Ravi</Typography>
                    </Box>
                    <Box onClick={popSheet} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}><CloseIcon sx={{ fontSize: 18 }} /></Box>
                  </Stack>
                  <Box sx={{ my: 1.4, borderTop: `1px solid ${tokens.border}` }} />
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pb: 1.4, borderBottom: `1px solid ${tokens.border}` }}>
                    <Avatar sx={{ width: 46, height: 46, bgcolor: '#7c3aed', color: '#fff', fontWeight: 700 }}>R</Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ ...textSx(tokens, 0.5, 700) }}>Ravi</Typography>
                      {config.chat_feature.is_user_presence && (
                        <Typography sx={{ ...textSx(tokens, -1.5, 500), color: tokens.secondary }}>Offline</Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack spacing={0.3} sx={{ mt: 1 }}>
                    {[
                      { label: 'Change scope', icon: <BoltOutlinedIcon sx={{ fontSize: 18 }} />, danger: false, onClick: () => pushSheet('scope') },
                      ...(config.chat_feature.is_ban_user ? [{ label: 'Ban participant', icon: <LockOutlinedIcon sx={{ fontSize: 18 }} />, danger: false, onClick: () => pushSheet('ban') }] : []),
                      { label: 'Transfer ownership', icon: <ArrowForwardIcon sx={{ fontSize: 18 }} />, danger: false, onClick: () => pushSheet('transfer') },
                      { label: 'Remove from group', icon: <DeleteOutlineIcon sx={{ fontSize: 18 }} />, danger: true, onClick: () => onOpenModal('confirm') },
                    ].map((item) => (
                      <Stack key={item.label} direction="row" spacing={1.3} alignItems="center" onClick={item.onClick} sx={{ px: 0.4, py: 1, cursor: 'pointer', color: item.danger ? '#f05252' : tokens.primary, borderRadius: '8px', '&:hover': { backgroundColor: withAlpha(tokens.brand, '10') } }}>
                        {item.icon}
                        <Typography sx={{ ...textSx(tokens, 0, 600), color: 'inherit' }}>{item.label}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </>,
                400
              )}
              {sheet === 'scope' && sheetCard(
                <Stack alignItems="center" sx={{ textAlign: 'center', py: 0.5 }}>
                  <Box sx={{ width: 54, height: 54, borderRadius: '16px', backgroundColor: '#f4f4f8', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.4 }}>
                    <BoltOutlinedIcon />
                  </Box>
                  <Typography sx={{ ...textSx(tokens, 2, 700) }}>Change Scope</Typography>
                  <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mt: 0.7, mb: 1.6, maxWidth: 320 }}>
                    You can change scope for the group member to manage group permissions and responsibilities.
                  </Typography>
                  <Stack spacing={1.3} sx={{ width: '100%', textAlign: 'left', border: `1px solid ${tokens.border}`, borderRadius: '14px', p: 1.5 }}>
                    {([
                      ['Participant', 'Reads and participates in conversations.'],
                      ['Admin', 'Manages group settings and members'],
                      ['Moderator', 'Enforces group rules by moderating messages'],
                    ] as const).map(([label, hint]) => (
                      <Stack key={label} direction="row" justifyContent="space-between" alignItems="center" onClick={() => setScope(label)} sx={{ cursor: 'pointer' }}>
                        <Box>
                          <Typography sx={{ ...textSx(tokens, -0.5, 700) }}>{label}</Typography>
                          <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>{hint}</Typography>
                        </Box>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${scope === label ? '#60a5fa' : tokens.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {scope === label && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#60a5fa' }} />}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ width: '100%', mt: 2 }}>
                    <Box onClick={popSheet} sx={{ px: 2, py: 0.8, borderRadius: '10px', border: `1px solid ${tokens.border}`, cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 600) }}>Cancel</Typography>
                    </Box>
                    <Box onClick={popSheet} sx={{ px: 2.2, py: 0.8, borderRadius: '10px', backgroundColor: tokens.brand, cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 700), color: '#fff' }}>Save</Typography>
                    </Box>
                  </Stack>
                </Stack>,
                420
              )}
              {sheet === 'add' && sheetCard(
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ ...textSx(tokens, -1.5, 700), color: tokens.brand }}>{selected?.name}</Typography>
                      <Typography sx={{ ...textSx(tokens, 4, 700) }}>Add people</Typography>
                    </Box>
                    <Box onClick={popSheet} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}><CloseIcon sx={{ fontSize: 18 }} /></Box>
                  </Stack>
                  <Box sx={{ my: 1.5, height: 40, borderRadius: '10px', border: `1px solid ${tokens.border}` }} />
                  <Stack direction="row" spacing={1.2} alignItems="center" onClick={() => setPicked((value) => !value)} sx={{ px: 1.2, py: 1, borderRadius: '12px', border: `1px solid ${tokens.border}`, cursor: 'pointer' }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '4px', border: `1.5px solid ${picked ? tokens.brand : tokens.border}`, backgroundColor: picked ? tokens.brand : 'transparent' }} />
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 700 }}>S</Avatar>
                    <Typography sx={{ ...textSx(tokens, -0.5, 600) }}>Sachin</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2.5 }}>
                    <Box onClick={popSheet} sx={{ px: 2, py: 0.8, borderRadius: '10px', border: `1px solid ${tokens.border}`, cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 600) }}>Back</Typography>
                    </Box>
                    <Box onClick={popSheet} sx={{ px: 2, py: 0.8, borderRadius: '10px', backgroundColor: tokens.brand, cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 700), color: '#fff' }}>Add selected</Typography>
                    </Box>
                  </Stack>
                </>
              )}
              {sheet === 'ban' && sheetCard(
                <Stack alignItems="center" sx={{ textAlign: 'center' }}>
                  <Box sx={{ width: 54, height: 54, borderRadius: '50%', backgroundColor: 'rgba(240,82,82,0.16)', color: '#f05252', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.2 }}>
                    <BlockOutlinedIcon />
                  </Box>
                  <Typography sx={{ ...textSx(tokens, 2, 700) }}>Ban Ravi</Typography>
                  <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mt: 0.8, mb: 1.5, maxWidth: 300 }}>
                    Are you sure you want to ban this participant? They will be removed from the group and cannot rejoin unless unbanned.
                  </Typography>
                  <Box
                    component="textarea"
                    value={banReason}
                    onChange={(event) => setBanReason(event.target.value.slice(0, 500))}
                    placeholder="Enter reason for banning this participant..."
                    sx={{
                      width: '100%',
                      minHeight: 88,
                      resize: 'none',
                      borderRadius: '12px',
                      border: `1px solid ${tokens.border}`,
                      backgroundColor: 'transparent',
                      color: tokens.primary,
                      fontFamily: tokens.fontFamily,
                      fontSize: tokens.fontSize - 1,
                      p: 1.2,
                      outline: 'none',
                    }}
                  />
                  <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary, alignSelf: 'flex-end', mt: 0.4 }}>{banReason.length}/500</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Box onClick={popSheet} sx={{ px: 2.2, py: 0.85, borderRadius: '10px', border: `1px solid ${tokens.border}`, cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 600) }}>Cancel</Typography>
                    </Box>
                    <Box onClick={popSheet} sx={{ px: 2.2, py: 0.85, borderRadius: '10px', backgroundColor: '#f05252', cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 700), color: '#fff' }}>Ban User</Typography>
                    </Box>
                  </Stack>
                </Stack>
              )}
              {sheet === 'transfer' && sheetCard(
                <Stack alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
                  <Box sx={{ width: 54, height: 54, borderRadius: '50%', backgroundColor: 'rgba(240,82,82,0.16)', color: '#f05252', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.4 }}>
                    <WarningAmberIcon />
                  </Box>
                  <Typography sx={{ ...textSx(tokens, 2, 700) }}>Transfer ownership?</Typography>
                  <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mt: 0.8, mb: 2 }}>Transfer group ownership to Ravi?</Typography>
                  <Stack direction="row" spacing={1}>
                    <Box onClick={popSheet} sx={{ px: 2.2, py: 0.85, borderRadius: '10px', border: `1px solid ${tokens.border}`, cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 600) }}>Cancel</Typography>
                    </Box>
                    <Box onClick={popSheet} sx={{ px: 2.4, py: 0.85, borderRadius: '10px', backgroundColor: '#f05252', cursor: 'pointer' }}>
                      <Typography sx={{ ...textSx(tokens, -1, 700), color: '#fff' }}>Transfer</Typography>
                    </Box>
                  </Stack>
                </Stack>,
                380
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ScheduledScreen: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  compact?: boolean;
  onNavigate: (id: KitScreenId) => void;
  onOpenModal: (id: KitModalId) => void;
}> = ({ tokens, config, compact, onOpenModal }) => {
  const rows = SCHEDULED.filter((item) => (item.type === 'video' ? showVideoCalls(config) : showVoiceCalls(config)));
  const [selectedId, setSelectedId] = useState(rows[0]?.id || '');
  const [showDetail, setShowDetail] = useState(!compact);
  const [formOpen, setFormOpen] = useState(false);
  const selected = rows.find((item) => item.id === selectedId) || rows[0];
  const detailVisible = Boolean(selected) && (compact ? showDetail : true);
  const canSchedule = config.call_feature.is_schedule_calls;
  const iconBtn = {
    width: 32,
    height: 32,
    borderRadius: '10px',
    border: `1.5px solid ${withAlpha(tokens.brand, '35')}`,
    color: tokens.brand,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  } as const;
  const canJoin = selected && (selected.status === 'ongoing' || config.call_feature.is_flexible_join_schedule_calls);

  return (
    <Box sx={{ height: '100%', minWidth: 0, display: 'flex', position: 'relative' }}>
      <Box
        sx={{
          width: detailVisible && !compact ? { xs: 268, lg: 300 } : '100%',
          minWidth: detailVisible && !compact ? { xs: 248, lg: 280 } : 0,
          display: detailVisible && compact ? 'none' : 'flex',
          height: '100%',
          flexDirection: 'column',
          borderRight: `1px solid ${tokens.border}`,
          backgroundColor: tokens.surface,
          flexShrink: 0,
        }}
      >
        <Box sx={{ px: 2.25, pt: 2.25, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.75 }}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ ...labelSx(tokens), mb: 0.45 }}>Communications</Typography>
              <Typography sx={{ ...textSx(tokens, 6, 700), lineHeight: 1.1 }}>Scheduled</Typography>
            </Box>
            {canSchedule && (
              <Box onClick={() => setFormOpen(true)} sx={iconBtn}><AddIcon sx={{ fontSize: 18 }} /></Box>
            )}
          </Stack>
          {config.chat_feature.is_search_message && (
            <Box sx={{ height: 40, px: 1.5, borderRadius: '8px', backgroundColor: tokens.isDark ? '#1F2937' : '#F3F4F6', border: `1px solid ${tokens.border}`, display: 'flex', alignItems: 'center', gap: 1, color: tokens.muted }}>
              <SearchOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.muted, fontFamily: tokens.fontFamily }}>Search chats</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ borderTop: `1px solid ${tokens.border}` }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.25, pt: 1.5, pb: 0.75 }}>
          <Typography sx={labelSx(tokens)}>Upcoming & recent</Typography>
          <RefreshOutlinedIcon sx={{ fontSize: 16, color: tokens.secondary }} />
        </Stack>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1.5, ...hideScrollbarSx }}>
          <Stack spacing={1}>
            {rows.map((item) => {
              const active = item.id === selected?.id;
              const live = item.status === 'ongoing';
              return (
                <Box
                  key={item.id}
                  onClick={() => {
                    setSelectedId(item.id);
                    setShowDetail(true);
                  }}
                  sx={{
                    ...selectedRowSx(tokens, active),
                    p: 1.25,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <Stack direction="row" spacing={1.1} alignItems="flex-start">
                    <Avatar sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: item.color, color: '#3f342b', fontWeight: 700, fontSize: 16 }}>{item.initial}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography noWrap sx={{ ...textSx(tokens, -0.5, 700) }}>{item.title}</Typography>
                        <Box sx={{ px: 0.8, py: 0.15, borderRadius: '999px', border: '1px solid #22c55e', color: '#22c55e', fontSize: 10, fontWeight: 700, fontFamily: tokens.fontFamily, textTransform: 'capitalize' }}>
                          {item.status}
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.45} alignItems="center" sx={{ mt: 0.35 }}>
                        <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: tokens.secondary }} />
                        <Typography sx={{ ...textSx(tokens, -2.3, 500), color: tokens.secondary }}>{item.when}</Typography>
                      </Stack>
                      <Typography sx={{ ...textSx(tokens, -2.2, 500), color: tokens.secondary, mt: 0.2 }}>{item.people}</Typography>
                      {live && (
                        <Box
                          onClick={(event) => {
                            event.stopPropagation();
                            if (canJoin) onOpenModal('in-call');
                          }}
                          sx={{ mt: 1, width: 'fit-content', px: 1.3, py: 0.45, borderRadius: '999px', backgroundColor: tokens.brand, color: '#fff', display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer' }}
                        >
                          <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
                          <Typography sx={{ fontSize: tokens.fontSize - 2, fontWeight: 700, fontFamily: tokens.fontFamily, color: '#fff' }}>Resume</Typography>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
        <ProfileFooter tokens={tokens} showPresence={config.chat_feature.is_user_presence} />
      </Box>

      {selected && detailVisible && (
        <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: tokens.isDark ? tokens.surface : tokens.chatBg }}>
          <Stack direction="row" alignItems="center" spacing={1.1} sx={{ px: 1.75, py: 1.25, borderBottom: `1px solid ${tokens.border}` }}>
            <Box onClick={() => compact && setShowDetail(false)} sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.secondary, cursor: compact ? 'pointer' : 'default' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </Box>
            <Avatar sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#bbf7d0', color: '#3f342b', fontWeight: 700, fontSize: 14 }}>{selected.initial}</Avatar>
            <Box sx={{ minWidth: 0, textAlign: 'left' }}>
              <Typography noWrap sx={{ ...textSx(tokens, -0.5, 700) }}>{selected.title}</Typography>
              <Stack direction="row" spacing={0.45} alignItems="center">
                <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: tokens.secondary }} />
                <Typography sx={{ ...textSx(tokens, -2.2, 500), color: tokens.secondary }}>{selected.when}</Typography>
              </Stack>
            </Box>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, ...hideScrollbarSx }}>
            <Box sx={{ height: '100%', minHeight: 420, borderRadius: '16px', border: `1px solid ${tokens.border}`, backgroundColor: tokens.isDark ? '#121a2b' : tokens.surface, p: 2.25, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <Box sx={{ width: 44, height: 44, borderRadius: '12px', backgroundColor: tokens.brand, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {selected.type === 'video' ? <VideocamOutlinedIcon /> : <CallOutlinedIcon />}
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ ...textSx(tokens, 5, 700), lineHeight: 1.1 }}>{selected.title}</Typography>
                    <Stack spacing={0.55} sx={{ mt: 1.3 }}>
                      <Box sx={{ width: 36, height: 3, borderRadius: 2, backgroundColor: tokens.border }} />
                      <Box sx={{ width: 28, height: 3, borderRadius: 2, backgroundColor: tokens.border }} />
                      <Box sx={{ width: 18, height: 3, borderRadius: 2, backgroundColor: tokens.border }} />
                    </Stack>
                  </Box>
                </Stack>
                <Box sx={{ px: 1, py: 0.25, borderRadius: '999px', border: '1px solid #22c55e', color: '#22c55e', fontSize: 12, fontWeight: 600, fontFamily: tokens.fontFamily, textTransform: 'capitalize' }}>
                  {selected.status}
                </Box>
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 0.8fr 1fr', gap: 1.5, mt: 2.5, pt: 1.6, borderTop: `1px solid ${tokens.border}` }}>
                <Box sx={{ textAlign: 'left' }}>
                  <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: tokens.secondary, mb: 0.6 }}>
                    <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography sx={labelSx(tokens)}>Date & time</Typography>
                  </Stack>
                  <Typography sx={{ ...textSx(tokens, -1, 700) }}>{selected.when}</Typography>
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: tokens.secondary, mb: 0.6 }}>
                    <VideocamOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography sx={labelSx(tokens)}>Call type</Typography>
                  </Stack>
                  <Typography sx={{ ...textSx(tokens, -1, 700) }}>{selected.type === 'video' ? 'Video call' : 'Audio call'}</Typography>
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: tokens.secondary, mb: 0.6 }}>
                    <PeopleOutlineIcon sx={{ fontSize: 14 }} />
                    <Typography sx={labelSx(tokens)}>Participants</Typography>
                  </Stack>
                  <Typography sx={{ ...textSx(tokens, -1, 700) }}>{selected.invited}</Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, pt: 1.6, borderTop: `1px solid ${tokens.border}`, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ ...labelSx(tokens), mb: 1.2 }}>Participants</Typography>
                <Stack direction="row" spacing={1}>
                  <Stack direction="row" spacing={0.7} alignItems="center" sx={{ px: 0.8, py: 0.45, borderRadius: '10px', backgroundColor: tokens.isDark ? '#1a2438' : tokens.panel }}>
                    <Avatar sx={{ width: 22, height: 22, borderRadius: '7px', bgcolor: '#F6C7A1', color: '#3f342b', fontSize: 11, fontWeight: 700 }}>R</Avatar>
                    <Typography sx={{ ...textSx(tokens, -1.5, 600) }}>Ravi · Host</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.7} alignItems="center" sx={{ px: 0.8, py: 0.45, borderRadius: '10px', backgroundColor: tokens.isDark ? '#1a2438' : tokens.panel }}>
                    <Avatar sx={{ width: 22, height: 22, borderRadius: '7px', bgcolor: '#DDD6FE', color: '#3f342b', fontSize: 11, fontWeight: 700 }}>A</Avatar>
                    <Typography sx={{ ...textSx(tokens, -1.5, 600) }}>Ajit</Typography>
                  </Stack>
                </Stack>
                <Box sx={{ flex: 1 }} />
                {canJoin && (
                  <Box onClick={() => onOpenModal('in-call')} sx={{ alignSelf: 'flex-end', px: 1.8, py: 0.85, borderRadius: '10px', backgroundColor: tokens.brand, cursor: 'pointer' }}>
                    <Typography sx={{ ...textSx(tokens, -1, 700), color: '#fff' }}>Join now</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {formOpen && (
        <Box onClick={() => setFormOpen(false)} sx={{ position: 'absolute', inset: 0, zIndex: 8, backgroundColor: 'rgba(2,6,23,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <ScheduleFormBody tokens={tokens} config={config} onClose={() => setFormOpen(false)} />
        </Box>
      )}
    </Box>
  );
};

const ModalGallery: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onOpenModal: (id: KitModalId) => void;
}> = ({ tokens, config, onOpenModal }) => {
  const items = modalItems(config);
  return (
    <Box sx={{ height: '100%', overflow: 'auto', ...hideScrollbarSx }}>
      <ScreenHeader tokens={tokens} kicker="Prototype" title="Every modal" />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1, px: 1.75, pb: 2 }}>
        {items.map((item) => (
          <Box
            key={item.id}
            onClick={() => onOpenModal(item.id)}
            sx={{
              textAlign: 'left',
              p: 1.4,
              borderRadius: '12px',
              border: `1px solid ${tokens.border}`,
              cursor: 'pointer',
              backgroundColor: tokens.surface,
              '&:hover': { borderColor: withAlpha(tokens.brand, '55'), backgroundColor: tokens.softBrand },
            }}
          >
            <Typography sx={textSx(tokens, -0.5, 700)}>{item.label}</Typography>
            <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>{item.hint}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const KitMainScreen: React.FC<{
  screen: KitScreenId;
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  compact?: boolean;
  onNavigate: (id: KitScreenId, targetId?: string) => void;
  onOpenModal: (id: KitModalId) => void;
}> = ({ screen, tokens, config, compact, onNavigate, onOpenModal }) => {
  if (screen === 'peer-detail') return <PeerDetail tokens={tokens} config={config} onNavigate={onNavigate} onOpenModal={onOpenModal} />;
  if (screen === 'group-detail') return <GroupDetail tokens={tokens} config={config} onNavigate={onNavigate} onOpenModal={onOpenModal} />;
  if (screen === 'people') return <PeopleScreen tokens={tokens} config={config} compact={compact} onNavigate={onNavigate} />;
  if (screen === 'calls') return <CallsScreen tokens={tokens} config={config} compact={compact} onOpenModal={onOpenModal} />;
  if (screen === 'groups') return <GroupsScreen tokens={tokens} config={config} compact={compact} onNavigate={onNavigate} onOpenModal={onOpenModal} />;
  if (screen === 'scheduled') return <ScheduledScreen tokens={tokens} config={config} compact={compact} onNavigate={onNavigate} onOpenModal={onOpenModal} />;
  if (screen === 'schedule-form') {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, backgroundColor: tokens.isDark ? 'rgba(2,6,23,0.55)' : 'rgba(15,23,42,0.28)' }}>
        <ScheduleFormBody tokens={tokens} config={config} onClose={() => onNavigate(isKitScreenVisible('scheduled', config) ? 'scheduled' : 'inbox')} />
      </Box>
    );
  }
  return <ModalGallery tokens={tokens} config={config} onOpenModal={onOpenModal} />;
};

const CREATE_GROUP_PEOPLE = [
  { name: 'Mira Sharma', color: '#C4B5FD' },
  { name: 'Arjun Mehta', color: '#FDBA74' },
  { name: 'Support assistant', color: '#93C5FD' },
];

const CreateGroupForm: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onClose: () => void;
}> = ({ tokens, config, onClose }) => {
  const types = [
    config.chat_feature.is_private_and_protected_group ? 'Protected group' : '',
    config.chat_feature.is_public_group ? 'Public group' : '',
    config.chat_feature.is_private_and_protected_group ? 'Private group' : '',
  ].filter(Boolean);
  const [groupType, setGroupType] = useState(types[0] || 'Protected group');
  const [typeOpen, setTypeOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({ 'Mira Sharma': true });
  const showPassword = groupType.toLowerCase().includes('protected');
  const fieldBg = tokens.isDark ? '#1F2937' : '#F4F6F8';
  const labelStyle = {
    fontWeight: 700,
    fontSize: tokens.fontSize - 2.5,
    color: tokens.primary,
    fontFamily: tokens.fontFamily,
    textAlign: 'left' as const,
    mb: 0.75,
  };

  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 2.25, textAlign: 'left' }}>
      <Typography sx={labelStyle}>Group Name</Typography>
      <Box
        component="input"
        placeholder="Group Name"
        maxLength={40}
        sx={{
          width: '100%',
          height: 44,
          px: 1.6,
          border: 'none',
          outline: 'none',
          borderRadius: '10px',
          backgroundColor: fieldBg,
          color: tokens.primary,
          fontSize: tokens.fontSize - 2.5,
          fontFamily: tokens.fontFamily,
          '&::placeholder': { color: '#9CA3AF', opacity: 1 },
        }}
      />
      <Typography sx={{ mt: 0.7, mb: 2, fontSize: tokens.fontSize - 4, color: tokens.secondary, fontFamily: tokens.fontFamily, textAlign: 'left' }}>
        Maximum 40 characters
      </Typography>

      <Typography sx={labelStyle}>Group Type</Typography>
      <Box sx={{ position: 'relative', mb: showPassword ? 2 : 2 }}>
        <Box
          onClick={() => setTypeOpen((open) => !open)}
          sx={{
            height: 44,
            px: 1.6,
            borderRadius: '10px',
            backgroundColor: fieldBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            color: tokens.primary,
            fontSize: tokens.fontSize - 2.5,
            fontFamily: tokens.fontFamily,
          }}
        >
          {groupType}
          <KeyboardArrowDownIcon sx={{ fontSize: 20, color: tokens.secondary }} />
        </Box>
        {typeOpen && types.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 'calc(100% + 4px)',
              zIndex: 2,
              borderRadius: '10px',
              backgroundColor: tokens.surface,
              border: `1px solid ${tokens.border}`,
              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
              overflow: 'hidden',
            }}
          >
            {types.map((type) => (
              <Box
                key={type}
                onClick={() => {
                  setGroupType(type);
                  setTypeOpen(false);
                }}
                sx={{
                  px: 1.6,
                  py: 1,
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: type === groupType ? tokens.brand : tokens.primary,
                  fontFamily: tokens.fontFamily,
                  fontSize: tokens.fontSize - 2.5,
                  '&:hover': { backgroundColor: withAlpha(tokens.brand, '10') },
                }}
              >
                {type}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {showPassword && (
        <>
          <Typography sx={labelStyle}>Group Password</Typography>
          <Box
            component="input"
            placeholder="Create a protected password"
            sx={{
              width: '100%',
              height: 44,
              px: 1.6,
              mb: 2,
              border: 'none',
              outline: 'none',
              borderRadius: '10px',
              backgroundColor: fieldBg,
              color: tokens.primary,
              fontSize: tokens.fontSize - 2.5,
              fontFamily: tokens.fontFamily,
              '&::placeholder': { color: '#9CA3AF', opacity: 1 },
            }}
          />
        </>
      )}

      <Typography sx={labelStyle}>Participants</Typography>
      <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        {CREATE_GROUP_PEOPLE.map((person) => {
          const checked = Boolean(selected[person.name]);
          return (
            <Stack
              key={person.name}
              direction="row"
              spacing={1.25}
              alignItems="center"
              onClick={() => setSelected((current) => ({ ...current, [person.name]: !current[person.name] }))}
              sx={{ px: 1.4, py: 1.05, cursor: 'pointer', '&:hover': { backgroundColor: withAlpha(tokens.brand, '08') } }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '4px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: checked ? tokens.brand : 'transparent',
                  border: `1.5px solid ${checked ? tokens.brand : '#D1D5DB'}`,
                  color: '#ffffff',
                }}
              >
                {checked && <CheckIcon sx={{ fontSize: 14 }} />}
              </Box>
              <Avatar sx={{ width: 28, height: 28, bgcolor: person.color, color: '#4B5563', fontSize: 10, fontWeight: 700 }}>
                {person.name.charAt(0)}
              </Avatar>
              <Typography sx={{ fontSize: tokens.fontSize - 2.5, fontWeight: 600, color: tokens.primary, fontFamily: tokens.fontFamily }}>
                {person.name}
              </Typography>
            </Stack>
          );
        })}
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2.25 }}>
        <Box
          onClick={onClose}
          sx={{
            px: 1.8,
            py: 0.85,
            borderRadius: '10px',
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.surface,
            color: tokens.primary,
            fontFamily: tokens.fontFamily,
            fontWeight: 600,
            fontSize: tokens.fontSize - 2.5,
            cursor: 'pointer',
          }}
        >
          Cancel
        </Box>
        <Box
          onClick={onClose}
          sx={{
            px: 1.8,
            py: 0.85,
            borderRadius: '10px',
            backgroundColor: tokens.brand,
            color: '#ffffff',
            fontFamily: tokens.fontFamily,
            fontWeight: 700,
            fontSize: tokens.fontSize - 2.5,
            cursor: 'pointer',
          }}
        >
          Create group
        </Box>
      </Stack>
    </Box>
  );
};

const SEARCH_INK = '#1B2740';
const SEARCH_SLATE = '#6B7F99';
const SEARCH_LINE = '#DBE6F1';
const SEARCH_PURPLE = '#7167DC';
const SEARCH_PURPLE_SOFT = '#F4F3FC';
const SEARCH_PURPLE_LINE = '#B6BAEA';

const SEARCH_FILTERS: Array<{ id: string; label: string; icon: React.ReactNode }> = [
  { id: 'all', label: 'All', icon: <SearchOutlinedIcon sx={{ fontSize: 15 }} /> },
  { id: 'messages', label: 'Messages', icon: <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} /> },
  { id: 'photos', label: 'Photos', icon: <ImageOutlinedIcon sx={{ fontSize: 15 }} /> },
  { id: 'videos', label: 'Videos', icon: <VideocamOutlinedIcon sx={{ fontSize: 15 }} /> },
  { id: 'audio', label: 'Audio', icon: <MicNoneIcon sx={{ fontSize: 15 }} /> },
  { id: 'documents', label: 'Documents', icon: <DescriptionOutlinedIcon sx={{ fontSize: 15 }} /> },
  { id: 'other', label: 'Other', icon: <AttachFileOutlinedIcon sx={{ fontSize: 15 }} /> },
];

const SEARCH_HITS = [
  {
    id: 'mira-voice',
    kind: 'messages',
    title: 'Launch team',
    time: '09:48',
    lines: ['Mira: Great. Include the voice and', 'video states too.'],
  },
  {
    id: 'you-concepts',
    kind: 'messages',
    title: 'You',
    time: '09:45',
    lines: ["I'll bring the desktop and mobile", 'chat concepts.'],
  },
  {
    id: 'arjun-review',
    kind: 'messages',
    title: 'Launch team',
    time: '09:42',
    lines: ['Arjun: Final review is on Friday at 2', 'PM.'],
  },
];

const SEARCH_FILTER_ROWS = [
  ['all', 'messages', 'photos'],
  ['videos', 'audio', 'documents'],
  ['other'],
];

const SearchConversationScreen: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const face = '"Inter", "Segoe UI", sans-serif';
  const text = { fontFamily: face, color: SEARCH_INK };

  useEffect(() => {
    const id = 'sampark-search-font-inter';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  const results = SEARCH_HITS.filter((item) => {
    if (filter !== 'all' && item.kind !== filter) return false;
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return `${item.title} ${item.lines.join(' ')}`.toLowerCase().includes(needle);
  });

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 6,
        backgroundColor: '#FFFFFF',
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
        fontFamily: face,
        '& .MuiTypography-root': { fontFamily: face },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 360,
          height: '100%',
          overflowY: 'auto',
          px: '24px',
          pt: '28px',
          pb: '24px',
          boxSizing: 'border-box',
          ...hideScrollbarSx,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            sx={{
              ...text,
              color: SEARCH_SLATE,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.06em',
              lineHeight: 1,
            }}
          >
            SEARCH CONVERSATION
          </Typography>
          <Box onClick={onClose} sx={{ color: SEARCH_SLATE, display: 'flex', cursor: 'pointer', mr: '-2px' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </Box>
        </Stack>

        <Typography sx={{ ...text, color: SEARCH_SLATE, fontSize: 13.5, fontWeight: 500, lineHeight: 1.35, mt: '16px', whiteSpace: 'nowrap' }}>
          Messages and attachments in{' '}
          <Box component="span" sx={{ color: SEARCH_INK, fontWeight: 700 }}>
            Launch team.
          </Box>
        </Typography>

        <Box
          sx={{
            mt: '16px',
            height: 48,
            px: '14px',
            borderRadius: '12px',
            border: `1px solid ${SEARCH_PURPLE}`,
            backgroundColor: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <SearchOutlinedIcon sx={{ fontSize: 18, color: SEARCH_SLATE }} />
          <Box
            component="input"
            value={query}
            autoFocus
            placeholder="Search this conversation"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
            sx={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: face,
              fontSize: 15,
              fontWeight: 500,
              color: SEARCH_INK,
              caretColor: SEARCH_INK,
              '&::placeholder': { color: '#A3B0BF', opacity: 1, fontWeight: 500 },
            }}
          />
        </Box>

        <Stack spacing="8px" sx={{ mt: '14px', alignItems: 'flex-start' }}>
          {SEARCH_FILTER_ROWS.map((row) => (
            <Stack key={row.join('-')} direction="row" spacing="8px">
              {row.map((id) => {
                const item = SEARCH_FILTERS.find((entry) => entry.id === id);
                if (!item) return null;
                const active = filter === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => setFilter(item.id)}
                    sx={{
                      height: 32,
                      px: '10px',
                      borderRadius: '10px',
                      border: `1px solid ${active ? SEARCH_PURPLE_LINE : SEARCH_LINE}`,
                      backgroundColor: active ? SEARCH_PURPLE_SOFT : '#FFFFFF',
                      color: active ? SEARCH_PURPLE : SEARCH_SLATE,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      fontFamily: face,
                      fontSize: 13.5,
                      fontWeight: 500,
                      lineHeight: 1,
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </Box>
                );
              })}
            </Stack>
          ))}
        </Stack>

        <Box sx={{ height: '1px', backgroundColor: SEARCH_LINE, mt: '18px' }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: '16px', mb: '12px' }}>
          <Typography sx={{ ...text, fontSize: 14, fontWeight: 700 }}>
            {results.length} {results.length === 1 ? 'result' : 'results'}
          </Typography>
          <Typography sx={{ ...text, color: SEARCH_SLATE, fontSize: 14, fontWeight: 500 }}>Newest first</Typography>
        </Stack>

        <Stack spacing="12px">
          {results.map((item) => (
            <Box
              key={item.id}
              sx={{
                border: `1px solid ${SEARCH_LINE}`,
                borderRadius: '14px',
                backgroundColor: '#FFFFFF',
                px: '16px',
                pt: '14px',
                pb: '12px',
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Typography sx={{ ...text, fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{item.title}</Typography>
                <Typography sx={{ ...text, color: SEARCH_SLATE, fontSize: 13, fontWeight: 500, flexShrink: 0, lineHeight: 1.4 }}>
                  {item.time}
                </Typography>
              </Stack>
              <Typography sx={{ ...text, fontSize: 14.5, fontWeight: 500, lineHeight: 1.45, mt: '8px' }}>
                {item.lines[0]}
                <br />
                {item.lines[1]}
              </Typography>
              <Box sx={{ height: '1px', backgroundColor: SEARCH_LINE, mt: '12px', mb: '10px' }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing="6px" sx={{ color: SEARCH_SLATE, minWidth: 0 }}>
                  <LinkOutlinedIcon sx={{ fontSize: 15 }} />
                  <Typography sx={{ ...text, color: SEARCH_SLATE, fontSize: 13, fontWeight: 500 }}>In this conversation</Typography>
                </Stack>
                <ArrowForwardIcon sx={{ fontSize: 16, color: SEARCH_SLATE }} />
              </Stack>
            </Box>
          ))}
          {results.length === 0 && (
            <Typography sx={{ ...text, color: SEARCH_SLATE, fontSize: 14, fontWeight: 500, textAlign: 'center', py: '28px' }}>
              No matches in this conversation.
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

const ModalCard: React.FC<{
  tokens: ThemeTokens;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  largeTitle?: boolean;
}> = ({ tokens, title, onClose, children, width = 380, largeTitle }) => (
  <Box
    onClick={(event) => event.stopPropagation()}
    sx={{
      width: '100%',
      maxWidth: width,
      maxHeight: '92%',
      overflow: 'auto',
      backgroundColor: tokens.surface,
      color: tokens.primary,
      borderRadius: '16px',
      border: `1px solid ${tokens.border}`,
      boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
      fontFamily: tokens.fontFamily,
      ...hideScrollbarSx,
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${tokens.border}` }}>
      <Typography sx={textSx(tokens, largeTitle ? 1 : 0.5, 800)}>{title}</Typography>
      <Box onClick={onClose} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}>
        <CloseIcon sx={{ fontSize: 18 }} />
      </Box>
    </Stack>
    {children}
  </Box>
);

const KitModalLayer: React.FC<{
  modal: KitModalId;
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onClose: () => void;
}> = ({ modal, tokens, config, onClose }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const body = useMemo(() => {
    const pad = { px: 2, py: 1.7, textAlign: 'left' as const };
    if (modal === 'schedule') return <ScheduleFormBody tokens={tokens} config={config} onClose={onClose} />;
    if (modal === 'reschedule') {
      return <ScheduleFormBody tokens={tokens} config={config} onClose={onClose} title="Reschedule call" submitLabel="Save" />;
    }
    if (modal === 'profile') {
      return (
        <Box sx={pad}>
          <Stack alignItems="center" spacing={0.6} sx={{ mb: 1.5 }}>
            <Initials name="You" color={withAlpha(tokens.brand, '22')} size={64} tokens={tokens} />
            <Typography sx={textSx(tokens, 1, 700)}>Your workspace</Typography>
            {config.chat_feature.is_user_presence && <Pill tokens={tokens} label="Online" tone="#118d77" />}
          </Stack>
          {['Available', 'Away', 'Busy', 'Do not disturb'].map((status) => (
            <Row key={status} tokens={tokens} onClick={onClose}>
              <Typography sx={textSx(tokens, -0.5, 600)}>{status}</Typography>
            </Row>
          ))}
          <Box sx={{ mt: 1.2 }}>
            <QuietButton tokens={tokens} label="Custom status" onClick={onClose} />
          </Box>
        </Box>
      );
    }
    if (modal === 'status') {
      return (
        <Box sx={pad}>
          <Field tokens={tokens} label="Status message" value="In a design review" />
          <Stack direction="row" spacing={0.7} sx={{ mb: 1.5 }}>
            {['In a meeting', 'Focusing', 'Be right back'].map((item) => (
              <Pill key={item} tokens={tokens} label={item} />
            ))}
          </Stack>
          <BrandButton tokens={tokens} label="Save status" onClick={onClose} full />
        </Box>
      );
    }
    if (modal === 'create-group') {
      return <CreateGroupForm tokens={tokens} config={config} onClose={onClose} />;
    }
    if (modal === 'password') {
      return (
        <Box sx={pad}>
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
            <Initials name="Care pod" color="#FBCFE8" tokens={tokens} group />
            <Box>
              <Typography sx={textSx(tokens, 0, 700)}>Care pod</Typography>
              <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>6 members · protected</Typography>
            </Box>
          </Stack>
          <Field tokens={tokens} label="Password" value="••••••••" />
          <BrandButton tokens={tokens} label="Join group" onClick={onClose} full />
        </Box>
      );
    }
    if (modal === 'group-options') {
      const options = [
        { label: 'Add participants', show: true },
        { label: 'Remove participants', show: config.chat_feature.is_ban_user || config.call_feature.is_remove_participants },
        { label: 'Leave group', show: true },
        { label: 'Transfer ownership', show: true },
      ].filter((item) => item.show);
      return (
        <Box sx={{ px: 1.2, py: 1.2 }}>
          {options.map((option) => (
            <Row key={option.label} tokens={tokens} onClick={() => onClose()}>
              <Typography sx={textSx(tokens, -0.5, 600)}>{option.label}</Typography>
            </Row>
          ))}
        </Box>
      );
    }
    if (modal === 'confirm') {
      return (
        <Box sx={{ ...pad, textAlign: 'center' }}>
          <BlockOutlinedIcon sx={{ fontSize: 28, color: '#df4b54', mb: 1 }} />
          <Typography sx={textSx(tokens, 1, 700)}>Block this contact?</Typography>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1.6 }}>
            You will stop receiving messages from Mira Sharma.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <QuietButton tokens={tokens} label="Cancel" onClick={onClose} />
            <BrandButton tokens={tokens} label="Block" onClick={onClose} />
          </Stack>
        </Box>
      );
    }
    if (modal === 'info') {
      return (
        <Box sx={{ ...pad, textAlign: 'center' }}>
          <InfoOutlinedIcon sx={{ color: tokens.brand, mb: 0.6 }} />
          <Typography sx={textSx(tokens, 1, 700)}>Group unavailable</Typography>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1.5 }}>
            This group was deleted by the owner.
          </Typography>
          <BrandButton tokens={tokens} label="OK" onClick={onClose} full />
        </Box>
      );
    }
    if (modal === 'incoming') {
      return (
        <Box sx={{ ...pad, textAlign: 'center' }}>
          <Typography sx={{ ...labelSx(tokens), textAlign: 'center' }}>Incoming call</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.4 }}>
            <Initials name="Mira Sharma" color="#DDD6FE" size={72} tokens={tokens} />
          </Box>
          <Typography sx={textSx(tokens, 2, 700)}>Mira Sharma</Typography>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1.6 }}>Voice call · waiting for you</Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <QuietButton tokens={tokens} label="Decline" danger onClick={onClose} />
            <BrandButton tokens={tokens} label="Accept" onClick={onClose} />
          </Stack>
        </Box>
      );
    }
    if (modal === 'outgoing') {
      return (
        <Box sx={{ ...pad, textAlign: 'center' }}>
          <Typography sx={{ ...labelSx(tokens), textAlign: 'center' }}>Calling</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.4 }}>
            <Initials name="Mira Sharma" color="#DDD6FE" size={72} tokens={tokens} />
          </Box>
          <Typography sx={textSx(tokens, 2, 700)}>Mira Sharma</Typography>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1.6 }}>Ringing…</Typography>
          <Box onClick={onClose} sx={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: '#df4b54', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', cursor: 'pointer' }}>
            <CallEndIcon />
          </Box>
        </Box>
      );
    }
    if (modal === 'in-call') {
      const controls = [
        { label: 'Mute', icon: <MicOffOutlinedIcon sx={{ fontSize: 18 }} />, show: config.call_feature.is_microphone_control || anyCall(config) },
        { label: 'Share', icon: <ScreenShareOutlinedIcon sx={{ fontSize: 18 }} />, show: config.call_feature.is_shared_screen },
        { label: 'Camera', icon: <VideocamOutlinedIcon sx={{ fontSize: 18 }} />, show: showVideoCalls(config) },
        { label: 'Invite', icon: <PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />, show: config.call_feature.is_invite_users },
        { label: 'Remove', icon: <DeleteOutlineIcon sx={{ fontSize: 18 }} />, show: config.call_feature.is_remove_participants },
      ].filter((item) => item.show);
      return (
        <Box sx={pad}>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1.2 }}>Launch team · 3 people</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
            {['You', 'Mira Sharma', 'Arjun Mehta'].slice(0, showVideoCalls(config) ? 3 : 2).map((name) => (
              <Box key={name} sx={{ borderRadius: '12px', backgroundColor: tokens.panel, minHeight: 72, display: 'flex', alignItems: 'flex-end', p: 1 }}>
                <Typography sx={textSx(tokens, -1.5, 700)}>{name}</Typography>
              </Box>
            ))}
          </Box>
          <Stack direction="row" spacing={0.8} justifyContent="center" flexWrap="wrap" useFlexGap>
            {controls.map((control) => (
              <Box key={control.label} sx={{ minWidth: 64, textAlign: 'center', color: tokens.secondary }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `1px solid ${tokens.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.4, color: tokens.primary }}>
                  {control.icon}
                </Box>
                <Typography sx={{ fontSize: Math.max(10, tokens.fontSize - 3), fontFamily: tokens.fontFamily }}>{control.label}</Typography>
              </Box>
            ))}
            <Box sx={{ minWidth: 64, textAlign: 'center', cursor: 'pointer' }} onClick={onClose}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#df4b54', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.4 }}>
                <CallEndIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontSize: Math.max(10, tokens.fontSize - 3), fontFamily: tokens.fontFamily, color: tokens.secondary }}>End</Typography>
            </Box>
          </Stack>
        </Box>
      );
    }
    if (modal === 'reactions') {
      return (
        <Box sx={pad}>
          <Stack direction="row" spacing={0.7} sx={{ mb: 1.2 }}>
            {['All 2', '❤️ 2'].map((tab, index) => (
              <Pill key={tab} tokens={tokens} label={tab} tone={index === 0 ? tokens.brand : tokens.secondary} />
            ))}
          </Stack>
          {['Mira Sharma', 'Arjun Mehta'].map((name) => (
            <Row key={name} tokens={tokens}>
              <Initials name={name} color="#DDD6FE" size={32} tokens={tokens} />
              <Typography sx={textSx(tokens, -0.5, 600)}>{name}</Typography>
            </Row>
          ))}
        </Box>
      );
    }
    if (modal === 'message-info') {
      return (
        <Box sx={pad}>
          {[
            ['Sent', '10:26'],
            ['Delivered', '10:26'],
            ['Read', '10:27'],
          ].map(([label, time]) => (
            <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.8, borderBottom: `1px solid ${tokens.border}` }}>
              <Typography sx={textSx(tokens, -0.5, 600)}>{label}</Typography>
              <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary }}>{time}</Typography>
            </Stack>
          ))}
        </Box>
      );
    }
    if (modal === 'delete-message' || modal === 'delete-conversation') return null;
    if (modal === 'thread') {
      return (
        <Box sx={pad}>
          <Box sx={{ p: 1.2, borderRadius: '10px', backgroundColor: tokens.panel, mb: 1.2 }}>
            <Typography sx={textSx(tokens, -1, 500)}>Can you share the conversation brief?</Typography>
          </Box>
          <Row tokens={tokens}>
            <Initials name="Mira Sharma" color="#DDD6FE" size={28} tokens={tokens} />
            <Typography sx={textSx(tokens, -1, 500)}>Uploading it now.</Typography>
          </Row>
          <Box sx={{ mt: 1.4, px: 1.3, py: 1, borderRadius: '20px', border: `1px solid ${tokens.border}`, color: tokens.secondary, fontSize: tokens.fontSize - 1 }}>
            Reply in thread
          </Box>
        </Box>
      );
    }
    if (modal === 'search') return null;
    if (modal === 'attach') {
      return (
        <Box sx={pad}>
          <Box sx={{ height: 92, borderRadius: '12px', backgroundColor: tokens.softBrand, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.2, color: tokens.brand }}>
            <DescriptionOutlinedIcon />
          </Box>
          <Field tokens={tokens} label="Caption" value="Sharing the brief" />
          <BrandButton tokens={tokens} label="Send" onClick={onClose} full />
        </Box>
      );
    }
    if (modal === 'unban') {
      return (
        <Box sx={pad}>
          <Typography sx={textSx(tokens, 0, 700)}>Ask to rejoin Care pod</Typography>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1.2 }}>Tell the owner why you should be allowed back.</Typography>
          <Field tokens={tokens} label="Message" value="I can follow the group guidelines." />
          <BrandButton tokens={tokens} label="Send request" onClick={onClose} full />
        </Box>
      );
    }
    if (modal === 'ownership') {
      return (
        <Box sx={{ px: 1.2, py: 1.2 }}>
          {MEMBERS.filter((member) => member.name !== 'You').map((member) => (
            <Row key={member.name} tokens={tokens} onClick={onClose}>
              <Initials name={member.name} color={member.color} size={34} tokens={tokens} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={textSx(tokens, -0.5, 600)}>{member.name}</Typography>
                <Typography sx={{ ...textSx(tokens, -2, 500), color: tokens.secondary }}>{member.role}</Typography>
              </Box>
            </Row>
          ))}
        </Box>
      );
    }
    if (modal === 'meeting-admin') {
      return (
        <Box sx={pad}>
          <Typography sx={{ ...textSx(tokens, -1, 500), color: tokens.secondary, mb: 1 }}>Choose the next meeting admin.</Typography>
          <Row tokens={tokens} onClick={onClose}>
            <Initials name="Mira Sharma" color="#DDD6FE" size={34} tokens={tokens} />
            <Typography sx={textSx(tokens, -0.5, 600)}>Mira Sharma</Typography>
          </Row>
          <Stack direction="row" spacing={1} sx={{ mt: 1.4 }}>
            {config.call_feature.is_microphone_control && <QuietButton tokens={tokens} label="Mute all" />}
            {config.call_feature.is_remove_participants && <QuietButton tokens={tokens} label="Remove" danger />}
          </Stack>
        </Box>
      );
    }
    return (
      <Box sx={pad}>
        <Field tokens={tokens} label="Group name" value="Launch team" />
        <BrandButton tokens={tokens} label="Save" onClick={onClose} full />
      </Box>
    );
  }, [modal, tokens, config, onClose]);

  if (modal === 'search') {
    return <SearchConversationScreen onClose={onClose} />;
  }

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 6,
        backgroundColor: tokens.isDark ? 'rgba(2,6,23,0.72)' : 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1.5,
      }}
    >
      {modal === 'delete-conversation' ? (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            width: 360,
            maxWidth: '100%',
            borderRadius: '18px',
            backgroundColor: tokens.surface,
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.22)',
            px: 3,
            py: 3.2,
            textAlign: 'center',
          }}
        >
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#FDECEC', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.6 }}>
            <DeleteIcon sx={{ fontSize: 28, color: '#ef4d4d' }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: tokens.fontSize + 2, fontFamily: tokens.fontFamily, color: tokens.primary, mb: 1 }}>
            Delete Conversation?
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize - 1, fontFamily: tokens.fontFamily, color: tokens.secondary, lineHeight: 1.5, mb: 2.2 }}>
            Would you like to delete this conversation? This will be deleted from all of your devices.
          </Typography>
          <Stack direction="row" spacing={1.2} justifyContent="center">
            <Box
              onClick={onClose}
              sx={{
                minWidth: 108,
                px: 2.2,
                py: 0.9,
                borderRadius: '10px',
                border: `1px solid ${tokens.border}`,
                cursor: 'pointer',
                fontFamily: tokens.fontFamily,
                fontWeight: 600,
                fontSize: tokens.fontSize - 0.5,
                color: tokens.primary,
              }}
            >
              Cancel
            </Box>
            <Box
              onClick={onClose}
              sx={{
                minWidth: 108,
                px: 2.2,
                py: 0.9,
                borderRadius: '10px',
                backgroundColor: '#ef4d4d',
                cursor: 'pointer',
                fontFamily: tokens.fontFamily,
                fontWeight: 700,
                fontSize: tokens.fontSize - 0.5,
                color: '#ffffff',
              }}
            >
              Delete
            </Box>
          </Stack>
        </Box>
      ) : modal === 'delete-message' ? (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            width: 300,
            borderRadius: '16px',
            backgroundColor: tokens.surface,
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.22)',
            overflow: 'hidden',
          }}
        >
          <Typography sx={{ px: 2.2, py: 1.7, fontWeight: 700, fontSize: tokens.fontSize + 0.5, fontFamily: tokens.fontFamily, color: tokens.primary, borderBottom: `1px solid ${tokens.border}`, textAlign: 'left' }}>
            Delete message?
          </Typography>
          {[
            { label: 'Delete for me', color: tokens.primary },
            { label: 'Delete for everyone', color: '#e11d48' },
            { label: 'Cancel', color: tokens.primary },
          ].map((option) => (
            <Box
              key={option.label}
              onClick={onClose}
              sx={{
                px: 2.2,
                py: 1.35,
                cursor: 'pointer',
                textAlign: 'left',
                color: option.color,
                fontFamily: tokens.fontFamily,
                fontSize: tokens.fontSize,
                '&:hover': { backgroundColor: withAlpha(tokens.brand, '08') },
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      ) : modal === 'schedule' || modal === 'reschedule' ? (
        body
      ) : (
        <ModalCard
          tokens={tokens}
          onClose={onClose}
          title={modalItems(config).find((item) => item.id === modal)?.label || 'Modal'}
          width={modal === 'create-group' ? 460 : modal === 'in-call' ? 440 : 380}
          largeTitle={modal === 'create-group'}
        >
          {body}
        </ModalCard>
      )}
    </Box>
  );
};

const CONVERSATIONS: ConversationItem[] = [
  {
    id: 'mira',
    name: 'Ravi',
    preview: 'Recent call',
    time: 'Just now',
    online: false,
    avatarColor: '#F6C7A1',
  },
  {
    id: 'launch',
    name: 'Launch team',
    preview: 'Arjun: Final review is on Friday.',
    time: '09:48',
    isGroup: true,
    members: 8,
    avatarColor: '#BBF7D0',
  },
  {
    id: 'arjun',
    name: 'Arjun Mehta',
    preview: 'See you on the call.',
    time: 'Yesterday',
    online: false,
    avatarColor: '#FED7AA',
  },
  {
    id: 'design',
    name: 'Design circle',
    preview: 'New files were shared.',
    time: 'Yesterday',
    isGroup: true,
    members: 12,
    avatarColor: '#BFDBFE',
  },
  {
    id: 'support',
    name: 'Support assistant',
    preview: 'How can we help today?',
    time: 'Yesterday',
    avatarColor: '#BFDBFE',
  },
];

const AvatarBubble: React.FC<{
  name: string;
  color: string;
  size?: number;
  online?: boolean;
  isGroup?: boolean;
  ink?: string;
  fontFamily?: string;
}> = ({ name, color, size = 40, online, isGroup, ink = '#475569', fontFamily }) => (
  <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: color,
        fontSize: size * 0.32,
        fontWeight: 600,
        color: ink,
        fontFamily: fontFamily || 'inherit',
      }}
    >
      {isGroup ? (
        <GroupsOutlinedIcon sx={{ fontSize: size * 0.48, color: ink }} />
      ) : (
        name
          .split(' ')
          .map((part) => part[0])
          .slice(0, 2)
          .join('')
          .toUpperCase()
      )}
    </Avatar>
    {online && (
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: Math.max(8, size * 0.22),
          height: Math.max(8, size * 0.22),
          borderRadius: '50%',
          backgroundColor: '#22C55E',
          border: '2px solid #FFFFFF',
        }}
      />
    )}
  </Box>
);

const ConversationRow: React.FC<{
  item: ConversationItem;
  active: boolean;
  tokens: ThemeTokens;
  showPresence?: boolean;
  onClick: () => void;
}> = ({ item, active, tokens, showPresence = true, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      ...selectedRowSx(tokens, active),
      display: 'flex',
      alignItems: 'center',
      gap: 1.75,
      px: 1.75,
      py: 1.35,
      cursor: 'pointer',
      boxSizing: 'border-box',
      fontFamily: tokens.fontFamily,
    }}
  >
    <AvatarBubble
      name={item.name}
      color={item.avatarColor}
      online={showPresence && item.online}
      isGroup={item.isGroup}
      size={44}
      ink={tokens.primary}
      fontFamily={tokens.fontFamily}
    />
    <Box
      sx={{
        minWidth: 0,
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
        <Typography
          noWrap
          sx={{
            fontWeight: 600,
            color: tokens.primary,
            fontSize: tokens.fontSize - 1,
            fontFamily: tokens.fontFamily,
            lineHeight: 1.25,
            textAlign: 'left',
          }}
        >
          {item.name}
        </Typography>
        <Typography
          noWrap
          sx={{
            mt: 0.35,
            color: tokens.secondary,
            fontSize: tokens.fontSize - 1.5,
            fontFamily: tokens.fontFamily,
            lineHeight: 1.3,
            textAlign: 'left',
          }}
        >
          {item.preview}
        </Typography>
      </Box>
      <Typography
        sx={{
          color: tokens.secondary,
          fontSize: tokens.fontSize - 2,
          fontFamily: tokens.fontFamily,
          lineHeight: 1.25,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          pt: 0.15,
        }}
      >
        {item.time}
      </Typography>
    </Box>
  </Box>
);

const MessagesListPanel: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  conversations: ConversationItem[];
  activeId: string;
  onSelect: (id: string) => void;
  onAdd?: () => void;
  compact?: boolean;
}> = ({ tokens, config, conversations, activeId, onSelect, onAdd, compact }) => (
  <Box
    sx={{
      width: compact ? '100%' : { xs: 268, lg: 300 },
      minWidth: compact ? 0 : { xs: 248, lg: 280 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.surface,
      borderRight: compact ? 'none' : `1px solid ${tokens.border}`,
    }}
  >
    <Box sx={{ px: 2.25, pt: 2.25, pb: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.75 }}>
        <Box sx={{ textAlign: 'left' }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: tokens.muted,
              mb: 0.45,
              textAlign: 'left',
            }}
          >
            Communications
          </Typography>
            <Typography
            sx={{
              fontWeight: 700,
              fontSize: tokens.fontSize + 6,
              fontFamily: tokens.fontFamily,
              color: tokens.primary,
              lineHeight: 1.1,
              textAlign: 'left',
            }}
          >
            Messages
          </Typography>
        </Box>
        {(config.chat_feature.is_public_group || config.chat_feature.is_private_and_protected_group) && (
          <Box
            onClick={onAdd}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              backgroundColor: tokens.surface,
              color: tokens.brand,
              border: `1.5px solid ${withAlpha(tokens.brand, '35')}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: onAdd ? 'pointer' : 'default',
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Stack>

      {config.chat_feature.is_search_message && (
        <Box
          sx={{
            height: 40,
            px: 1.5,
            borderRadius: '8px',
            backgroundColor: tokens.isDark ? '#1F2937' : '#F3F4F6',
            border: `1px solid ${tokens.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: tokens.muted,
            mb: 1.5,
          }}
        >
          <SearchOutlinedIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.muted }}>Search chats</Typography>
        </Box>
      )}
    </Box>

    <Box sx={{ borderTop: `1px solid ${tokens.border}`, mx: 0 }} />

    <Box sx={{ px: 2.25, pt: 1.5, pb: 0.75 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: tokens.muted,
          textAlign: 'left',
        }}
      >
        Recent conversations
      </Typography>
    </Box>

    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 1.75,
        pb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        ...hideScrollbarSx,
      }}
    >
      {conversations.map((item) => (
        <ConversationRow
          key={item.id}
          item={item}
          active={item.id === activeId}
          tokens={tokens}
          showPresence={config.chat_feature.is_user_presence}
          onClick={() => onSelect(item.id)}
        />
      ))}
    </Box>

    {!compact && <ProfileFooter tokens={tokens} showPresence={config.chat_feature.is_user_presence} />}
  </Box>
);

const ChatPane: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  conversation: ConversationItem;
  device: 'desktop' | 'mobile';
  onBack?: () => void;
  enriched?: boolean;
  onVoice?: () => void;
  onVideo?: () => void;
  onSearch?: () => void;
  onInfo?: () => void;
  onOpenModal?: (id: KitModalId) => void;
  onThreadChange?: (open: boolean) => void;
}> = ({ tokens, config, conversation, device, onBack, onVoice, onVideo, onSearch, onInfo, onOpenModal, onThreadChange }) => {
  const [openMessageMenu, setOpenMessageMenu] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<{ name: string; text: string } | null>(null);
  const [editing, setEditing] = useState<{ text: string } | null>(null);
  const [threadMessage, setThreadMessage] = useState<{ name: string; text: string; time: string } | null>(null);
  const isGroup = Boolean(conversation.isGroup);
  const showVoice = isGroup
    ? config.call_feature.is_group_voice_calls
    : config.call_feature.is_one_to_one_voice_calls;
  const showVideo = isGroup
    ? config.call_feature.is_group_video_calls
    : config.call_feature.is_one_to_one_video_calls;
  const iconBtnSx = {
    width: 34,
    height: 34,
    borderRadius: '50%',
    color: tokens.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as const;

  const renderOwnMenu = (menuKey: string, text: string, time: string) => {
    const items = [
      config.chat_feature.is_quote_reply ? { label: 'Reply', icon: <ReplyOutlinedIcon sx={{ fontSize: 16 }} />, id: 'reply' as const } : null,
      config.chat_feature.is_edit_message ? { label: 'Edit', icon: <EditOutlinedIcon sx={{ fontSize: 16 }} />, id: 'edit' as const } : null,
      config.chat_feature.is_thread ? { label: 'Thread', icon: <ForumOutlinedIcon sx={{ fontSize: 16 }} />, id: 'thread' as const } : null,
      { label: 'Copy', icon: <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />, id: 'copy' as const },
      config.chat_feature.is_read_and_delivered_receipts ? { label: 'Info', icon: <InfoOutlinedIcon sx={{ fontSize: 16 }} />, id: 'info' as const } : null,
      config.chat_feature.is_delete_chat ? { label: 'Delete', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />, id: 'delete' as const } : null,
    ].filter((item): item is { label: string; icon: React.ReactElement; id: 'reply' | 'edit' | 'thread' | 'copy' | 'info' | 'delete' } => Boolean(item));

    return (
      <Box
        className="bubble-dots"
        sx={{ position: 'absolute', right: 'calc(100% + 6px)', top: '50%', transform: 'translateY(-50%)', zIndex: 4 }}
      >
        <Box
          onClick={(event) => {
            event.stopPropagation();
            setOpenMessageMenu((current) => (current === menuKey ? null : menuKey));
          }}
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.isDark ? '#141c2e' : '#ffffff',
            color: tokens.secondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 16 }} />
        </Box>
        {openMessageMenu === menuKey && (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
              position: 'absolute',
              bottom: 'calc(100% + 6px)',
              right: 0,
              py: 0.6,
              px: 0.45,
              minWidth: 148,
              borderRadius: '14px',
              backgroundColor: tokens.isDark ? '#141c2e' : '#ffffff',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
            }}
          >
            {items.map((item) => (
              <Stack
                key={item.label}
                direction="row"
                spacing={1.1}
                alignItems="center"
                onClick={() => {
                  setOpenMessageMenu(null);
                  if (item.id === 'reply') {
                    setEditing(null);
                    setReplyTo({ name: 'You', text });
                  }
                  if (item.id === 'edit') {
                    setReplyTo(null);
                    setEditing({ text });
                  }
                  if (item.id === 'thread') {
                    setThreadMessage({ name: 'You', text, time });
                    onThreadChange?.(true);
                  }
                  if (item.id === 'info') onOpenModal?.('message-info');
                  if (item.id === 'delete') onOpenModal?.('delete-message');
                }}
                sx={{
                  px: 1,
                  py: 0.7,
                  cursor: 'pointer',
                  color: item.id === 'delete' ? '#e11d48' : tokens.primary,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: withAlpha(tokens.brand, '10') },
                }}
              >
                {item.icon}
                <Typography sx={{ fontSize: tokens.fontSize - 1, fontFamily: tokens.fontFamily, fontWeight: 500, color: 'inherit' }}>
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        minHeight: 0,
        position: 'relative',
        backgroundColor: tokens.surface,
        fontFamily: tokens.fontFamily,
      }}
    >
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          minHeight: device === 'mobile' ? 58 : 64,
          px: 2,
          py: 1.15,
          borderBottom: `1px solid ${tokens.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          flexShrink: 0,
          backgroundColor: tokens.surface,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            onClick={onBack}
            sx={{
              ...iconBtnSx,
              width: 28,
              height: 28,
              cursor: onBack ? 'pointer' : 'default',
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </Box>
          <AvatarBubble
            name={conversation.name}
            color={conversation.avatarColor}
            size={40}
            online={config.chat_feature.is_user_presence ? conversation.online : undefined}
            ink={tokens.primary}
            fontFamily={tokens.fontFamily}
          />
          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
            <Typography
              noWrap
              sx={{
                fontWeight: 600,
                color: tokens.primary,
                fontSize: tokens.fontSize,
                fontFamily: tokens.fontFamily,
                lineHeight: 1.25,
              }}
            >
              {conversation.name}
            </Typography>
            {(isGroup || config.chat_feature.is_user_presence) && (
              <Typography
                sx={{
                  color: tokens.secondary,
                  fontSize: tokens.fontSize - 1.5,
                  fontFamily: tokens.fontFamily,
                  mt: 0.2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.6,
                }}
              >
                {isGroup ? (
                  `${conversation.members || 12} members · group`
                ) : conversation.online ? (
                  <>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#22C55E', flexShrink: 0 }} />
                    Online
                  </>
                ) : (
                  'Offline'
                )}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.35} alignItems="center">
          {showVoice && (
            <Box sx={headerIconSx(tokens)} onClick={onVoice}>
              <CallOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
          )}
          {showVideo && (
            <Box sx={headerIconSx(tokens)} onClick={onVideo}>
              <VideocamOutlinedIcon sx={{ fontSize: 21 }} />
            </Box>
          )}
          {config.chat_feature.is_search_message && (
            <Box sx={headerIconSx(tokens)} onClick={onSearch}>
              <SearchOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
          )}
          <Box sx={headerIconSx(tokens)} onClick={onInfo}>
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          backgroundColor: tokens.isDark ? tokens.surface : tokens.chatBg,
          px: { xs: 1.5, md: 2 },
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.35,
          ...hideScrollbarSx,
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          spacing={0.7}
          onMouseLeave={() => setOpenMessageMenu((current) => (current === 'own-hello' ? null : current))}
          sx={{
            '& .bubble-dots': { opacity: 0, pointerEvents: 'none' },
            '&:hover .bubble-dots': { opacity: 1, pointerEvents: 'auto' },
          }}
        >
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ position: 'relative', width: 'fit-content', ml: 'auto' }}>
              <Box
                sx={{
                  px: 1.35,
                  py: 0.8,
                  borderRadius: '12px',
                  backgroundColor: tokens.brand,
                  color: '#FFFFFF',
                  fontSize: tokens.fontSize - 0.5,
                  fontFamily: tokens.fontFamily,
                  lineHeight: 1.4,
                }}
              >
                hello
              </Box>
              {renderOwnMenu('own-hello', 'hello', '11:27 AM')}
            </Box>
            {config.chat_feature.is_thread && (
              <Typography sx={{ mt: 0.35, color: tokens.secondary, fontSize: tokens.fontSize - 2.5, fontFamily: tokens.fontFamily }}>
                2 Threads
              </Typography>
            )}
          </Box>
          <Typography sx={{ color: tokens.secondary, fontSize: tokens.fontSize - 2.5, fontFamily: tokens.fontFamily, pb: 0.2 }}>
            11:27 AM
          </Typography>
          {config.chat_feature.is_read_and_delivered_receipts && (
            <DoneAllIcon sx={{ fontSize: 15, color: tokens.brand, mb: 0.15 }} />
          )}
        </Stack>

        {config.chat_feature.is_delete_chat && (
          <Box sx={{ alignSelf: 'flex-start', px: 1.35, py: 0.75, borderRadius: '12px', border: `1px dashed ${tokens.border}`, color: tokens.secondary, fontStyle: 'italic', fontSize: tokens.fontSize - 0.5, fontFamily: tokens.fontFamily }}>
            This message was deleted
          </Box>
        )}

        {config.chat_feature.is_media_file_sharing && (
          <Stack alignItems="flex-end" spacing={0.45} sx={{ alignSelf: 'flex-end' }}>
            <Box
              sx={{
                px: 1.2,
                py: 1,
                borderRadius: '12px',
                backgroundColor: tokens.isDark ? '#1a2438' : tokens.surface,
                border: `1px solid ${tokens.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 220,
                maxWidth: 280,
              }}
            >
              <DescriptionOutlinedIcon sx={{ fontSize: 22, color: tokens.brand }} />
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography noWrap sx={{ fontWeight: 600, fontSize: tokens.fontSize - 1, fontFamily: tokens.fontFamily, color: tokens.primary }}>
                  Simran kashyap -Full Stack D…
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize - 2.5, fontFamily: tokens.fontFamily, color: tokens.secondary }}>
                  83.6 KB
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ color: tokens.secondary, fontSize: tokens.fontSize - 2.5, fontFamily: tokens.fontFamily }}>
              11:27 AM
            </Typography>
          </Stack>
        )}

        {(
          [
            { text: isGroup ? 'New files were shared with the group.' : 'hello', time: '11:45 AM' },
            { text: isGroup ? 'Arjun: final review is on Friday.' : 'nsbdashdahsdasdh…', time: '11:45 AM' },
            { text: isGroup ? 'Please take a look when you can.' : 'asddasdasdasdasdasdad', time: '11:48 AM' },
            { text: isGroup ? '@You please review the brief.' : 'helkkjkjksdf', time: '11:48 AM', reaction: true },
          ] as Array<{ text: string; time: string; reaction?: boolean }>
        ).map((line) => (
          <Stack
            key={line.text}
            direction="row"
            alignItems="center"
            spacing={0.8}
            onMouseLeave={() => setOpenMessageMenu((current) => (current === line.text ? null : current))}
            sx={{
              alignSelf: 'flex-start',
              maxWidth: '100%',
              position: 'relative',
              '& .bubble-dots': { opacity: 0, pointerEvents: 'none' },
              '&:hover .bubble-dots': { opacity: 1, pointerEvents: 'auto' },
              '&:hover .bubble-time': { marginLeft: '36px' },
            }}
          >
            <AvatarBubble
              name={conversation.name}
              color={conversation.avatarColor}
              size={22}
              ink={tokens.primary}
              fontFamily={tokens.fontFamily}
            />
            <Box sx={{ position: 'relative', flexShrink: 0, maxWidth: 280 }}>
            <Box
              sx={{
                px: 1.35,
                py: 0.8,
                borderRadius: '12px',
                backgroundColor: tokens.isDark ? '#1a2438' : '#f3f6fb',
                border: `1px solid ${tokens.border}`,
                color: tokens.primary,
                fontSize: tokens.fontSize - 0.5,
                fontFamily: tokens.fontFamily,
                lineHeight: 1.4,
              }}
            >
              {line.text}
            </Box>
            <Box
              className="bubble-dots"
              sx={{
                position: 'absolute',
                left: 'calc(100% + 4px)',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
              }}
            >
              <Box
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenMessageMenu((current) => (current === line.text ? null : line.text));
                }}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  border: `1px solid ${tokens.border}`,
                  backgroundColor: tokens.isDark ? '#141c2e' : tokens.surface,
                  color: tokens.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              >
                <MoreHorizIcon sx={{ fontSize: 16 }} />
              </Box>
            {openMessageMenu === line.text && (
              <Box
                onClick={(event) => event.stopPropagation()}
                sx={{
                  py: 0.6,
                  px: 0.4,
                  minWidth: 118,
                  borderRadius: '12px',
                  backgroundColor: tokens.isDark ? '#141c2e' : tokens.surface,
                  border: `1px solid ${tokens.border}`,
                  boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
                }}
              >
                {[
                  config.chat_feature.is_quote_reply ? { label: 'Reply', icon: <ReplyOutlinedIcon sx={{ fontSize: 15 }} />, id: 'reply' as const } : null,
                  config.chat_feature.is_thread ? { label: 'Thread', icon: <ForumOutlinedIcon sx={{ fontSize: 15 }} />, id: 'thread' as const } : null,
                  { label: 'Copy', icon: <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />, id: 'copy' as const },
                  config.chat_feature.is_read_and_delivered_receipts ? { label: 'Info', icon: <InfoOutlinedIcon sx={{ fontSize: 15 }} />, id: 'info' as const } : null,
                  config.chat_feature.is_delete_chat ? { label: 'Delete', icon: <DeleteOutlineIcon sx={{ fontSize: 15 }} />, id: 'delete' as const } : null,
                ]
                  .filter((item): item is { label: string; icon: React.ReactElement; id: 'reply' | 'thread' | 'copy' | 'info' | 'delete' } => Boolean(item))
                  .map((item) => (
                    <Stack
                      key={item.label}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      onClick={() => {
                        setOpenMessageMenu(null);
                        if (item.id === 'reply') {
                          setEditing(null);
                          setReplyTo({ name: conversation.name, text: line.text });
                        }
                        if (item.id === 'thread') {
                          setThreadMessage({ name: conversation.name, text: line.text, time: line.time });
                          onThreadChange?.(true);
                        }
                        if (item.id === 'info') onOpenModal?.('message-info');
                        if (item.id === 'delete') onOpenModal?.('delete-message');
                      }}
                      sx={{
                        px: 1,
                        py: 0.55,
                        cursor: 'pointer',
                        color: item.id === 'delete' ? '#df4b54' : tokens.primary,
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: withAlpha(tokens.brand, '12') },
                      }}
                    >
                      {item.icon}
                      <Typography sx={{ fontSize: tokens.fontSize - 1.5, fontFamily: tokens.fontFamily, fontWeight: 500, color: 'inherit' }}>
                        {item.label}
                      </Typography>
                    </Stack>
                  ))}
              </Box>
            )}
            </Box>
            </Box>
            <Typography className="bubble-time" sx={{ color: tokens.secondary, fontSize: tokens.fontSize - 2.5, fontFamily: tokens.fontFamily, flexShrink: 0 }}>
              {line.time}
            </Typography>
            {line.reaction && config.chat_feature.is_message_reaction && (
              <Box sx={{ fontSize: 14, lineHeight: 1 }}>👍</Box>
            )}
          </Stack>
        ))}

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          spacing={0.7}
          onMouseLeave={() => setOpenMessageMenu((current) => (current === 'own-dfs' ? null : current))}
          sx={{
            '& .bubble-dots': { opacity: 0, pointerEvents: 'none' },
            '&:hover .bubble-dots': { opacity: 1, pointerEvents: 'auto' },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                px: 1.35,
                py: 0.8,
                borderRadius: '12px',
                backgroundColor: tokens.brand,
                color: '#FFFFFF',
                fontSize: tokens.fontSize - 0.5,
                fontFamily: tokens.fontFamily,
              }}
            >
              dfsdfsdfsdf
            </Box>
            {renderOwnMenu('own-dfs', 'dfsdfsdfsdf', '11:48 AM')}
          </Box>
          <Typography sx={{ color: tokens.secondary, fontSize: tokens.fontSize - 2.5, fontFamily: tokens.fontFamily }}>11:48 AM</Typography>
          {config.chat_feature.is_read_and_delivered_receipts && <DoneAllIcon sx={{ fontSize: 15, color: tokens.brand }} />}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.8} sx={{ alignSelf: 'flex-start' }}>
          <AvatarBubble name={conversation.name} color={conversation.avatarColor} size={22} ink={tokens.primary} fontFamily={tokens.fontFamily} />
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ px: 1.2, py: 0.75, borderRadius: '12px', border: `1px solid ${tokens.border}`, backgroundColor: tokens.isDark ? '#1a2438' : '#f3f6fb' }}>
            <CallReceivedIcon sx={{ fontSize: 16, color: tokens.brand }} />
            <Typography sx={{ fontSize: tokens.fontSize - 0.5, fontFamily: tokens.fontFamily, color: tokens.primary }}>Incoming call</Typography>
          </Stack>
        </Stack>

        {!isGroup && config.chat_feature.is_user_typing_indicator && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ alignSelf: 'flex-start', mt: 'auto' }}>
            <AvatarBubble name={conversation.name} color={conversation.avatarColor} size={32} ink={tokens.primary} fontFamily={tokens.fontFamily} />
            <Box
              sx={{
                px: 1.15,
                py: 0.85,
                borderRadius: '16px',
                backgroundColor: tokens.isDark ? '#1a2438' : '#eef1f6',
                display: 'flex',
                alignItems: 'center',
                gap: 0.45,
              }}
            >
              {[0, 1, 2].map((dot) => (
                <Box
                  key={dot}
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: '#94a3b8',
                  }}
                />
              ))}
            </Box>
            <Typography sx={{ color: tokens.secondary, fontSize: tokens.fontSize - 1, fontFamily: tokens.fontFamily }}>
              {conversation.name} is typing
            </Typography>
          </Stack>
        )}

      </Box>

      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: tokens.surface,
          borderTop: `1px solid ${tokens.border}`,
        }}
      >
        {editing && (
          <EditComposerBar tokens={tokens} preview={editing.text} onClose={() => setEditing(null)} />
        )}
        {replyTo && !editing && (
          <ReplyComposerBar
            tokens={tokens}
            name={replyTo.name}
            preview={replyTo.text}
            onClose={() => setReplyTo(null)}
          />
        )}
        <Box
          sx={{
            px: 1.5,
            py: 1.15,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {config.chat_feature.is_media_file_sharing && (
            <Box sx={composerSideIconSx(tokens)}>
              <AddIcon sx={{ fontSize: 18 }} />
            </Box>
          )}
          <MessageComposerField
            key={editing ? `edit-${editing.text}` : 'compose'}
            tokens={tokens}
            voiceEnabled={config.chat_feature.is_voice_message}
            initialDraft={editing?.text ?? ''}
          />
        </Box>
      </Box>
    </Box>
    {threadMessage && (
      <ThreadPanel
        tokens={tokens}
        name={threadMessage.name}
        text={threadMessage.text}
        time={threadMessage.time}
        avatarColor={threadMessage.name === conversation.name ? conversation.avatarColor : withAlpha(tokens.brand, '28')}
        overlay={device === 'mobile'}
        onClose={() => {
          setThreadMessage(null);
          onThreadChange?.(false);
        }}
      />
    )}
    </Box>
  );
};

const ConversationSidePanel: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  conversation: ConversationItem;
  onClose: () => void;
  onOpenModal: (id: KitModalId) => void;
}> = ({ tokens, config, conversation, onClose, onOpenModal }) => {
  const isGroup = Boolean(conversation.isGroup);
  const actions: Array<{ label: string; icon: React.ReactNode; onClick?: () => void }> = [
    { label: 'Chat', icon: <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} /> },
  ];
  const voice = isGroup ? config.call_feature.is_group_voice_calls : config.call_feature.is_one_to_one_voice_calls;
  const video = isGroup ? config.call_feature.is_group_video_calls : config.call_feature.is_one_to_one_video_calls;
  if (voice) actions.push({ label: 'Voice', icon: <CallOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') });
  if (video) actions.push({ label: 'Video', icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('outgoing') });
  if (config.chat_feature.is_search_message) {
    actions.push({ label: 'Search', icon: <SearchOutlinedIcon sx={{ fontSize: 16 }} />, onClick: () => onOpenModal('search') });
  }
  const online = Boolean(config.chat_feature.is_user_presence && conversation.online);

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderLeft: `1px solid ${tokens.border}`,
        backgroundColor: tokens.surface,
        overflowY: 'auto',
        px: 2,
        py: 1.75,
        fontFamily: tokens.fontFamily,
        textAlign: 'left',
        ...hideScrollbarSx,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: tokens.secondary }}>
          CONVERSATION DETAILS
        </Typography>
        <Box onClick={onClose} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </Box>
      </Stack>

      <Stack alignItems="center" sx={{ pt: 2.5, pb: 1.75, textAlign: 'center' }}>
        <AvatarBubble
          name={conversation.name}
          color={conversation.avatarColor}
          size={72}
          isGroup={isGroup}
          ink={tokens.primary}
          fontFamily={tokens.fontFamily}
        />
        <Typography sx={{ mt: 1.15, fontWeight: 700, fontSize: tokens.fontSize + 2, fontFamily: tokens.fontFamily, color: tokens.primary }}>
          {conversation.name}
        </Typography>
        {isGroup ? (
          <Typography sx={{ mt: 0.4, fontSize: tokens.fontSize - 1.5, color: tokens.secondary, fontFamily: tokens.fontFamily }}>
            {conversation.members || 8} members
          </Typography>
        ) : config.chat_feature.is_user_presence ? (
          <>
            <Typography sx={{ mt: 0.35, fontSize: tokens.fontSize - 1.5, color: tokens.secondary, fontFamily: tokens.fontFamily }}>
              {online ? 'Online now' : 'Offline'}
            </Typography>
            <Stack
              direction="row"
              spacing={0.55}
              alignItems="center"
              sx={{
                mt: 0.85,
                px: 1.05,
                py: 0.3,
                borderRadius: '99px',
                backgroundColor: online ? (tokens.isDark ? 'rgba(34,197,94,0.16)' : '#E7F8EE') : (tokens.isDark ? '#1F2937' : '#F3F4F6'),
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: online ? '#22C55E' : tokens.secondary }} />
              <Typography sx={{ fontSize: tokens.fontSize - 2, fontWeight: 600, color: online ? '#16A34A' : tokens.secondary, fontFamily: tokens.fontFamily }}>
                {online ? 'Online now' : 'Offline'}
              </Typography>
            </Stack>
          </>
        ) : null}
      </Stack>

      {!isGroup && <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />}

      <Typography sx={{ fontSize: tokens.fontSize - 0.5, fontWeight: 700, color: tokens.primary, fontFamily: tokens.fontFamily, mb: 1, textAlign: 'left' }}>
        Quick actions
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${actions.length}, 1fr)`, gap: 0.7, mb: 1.6 }}>
        {actions.map((action) => (
          <Box
            key={action.label}
            onClick={action.onClick}
            sx={{
              border: `1px solid ${tokens.border}`,
              borderRadius: '12px',
              py: 1,
              textAlign: 'center',
              cursor: 'pointer',
              color: tokens.secondary,
              backgroundColor: tokens.surface,
              '&:hover': {
                color: tokens.brand,
                borderColor: tokens.brand,
                backgroundColor: withAlpha(tokens.brand, '12'),
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.35, color: 'inherit' }}>{action.icon}</Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: tokens.fontFamily, color: 'inherit' }}>{action.label}</Typography>
          </Box>
        ))}
      </Box>

      {!isGroup && <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />}

      {config.chat_feature.is_media_file_sharing && (
        <Box sx={{ mb: 1.6, textAlign: 'left' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.1 }}>
            <Typography sx={{ fontSize: tokens.fontSize - 0.5, fontWeight: 700, fontFamily: tokens.fontFamily }}>Shared files</Typography>
            <Typography sx={{ fontSize: tokens.fontSize - 2, color: tokens.secondary, fontFamily: tokens.fontFamily }}>{isGroup ? '20' : '10'}</Typography>
          </Stack>
          {(isGroup
            ? [
                { name: 'images (4).jpg', meta: 'Sep 16 · 62 KB' },
                { name: 'images (3).jpg', meta: 'Sep 16 · 30 KB' },
              ]
            : [
                { name: 'images3.jpg', meta: 'Sep 25 · 30 KB' },
                { name: 'images3.jpg', meta: 'Sep 25 · 30 KB' },
              ]
          ).map((file, index) => (
            <Stack key={`${file.name}-${index}`} direction="row" spacing={1} alignItems="center" sx={{ mb: 1.05 }}>
              {isGroup ? (
                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: tokens.brand }} />
              ) : (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    backgroundColor: tokens.isDark ? '#1F2937' : withAlpha(tokens.brand, '10'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 18, color: tokens.brand }} />
                </Box>
              )}
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography noWrap sx={{ fontSize: tokens.fontSize - 1.5, fontWeight: 700, fontFamily: tokens.fontFamily }}>{file.name}</Typography>
                <Typography sx={{ fontSize: tokens.fontSize - 2.5, color: tokens.secondary, fontFamily: tokens.fontFamily }}>{file.meta}</Typography>
              </Box>
            </Stack>
          ))}
          <Typography sx={{ mt: 0.4, fontSize: tokens.fontSize - 1.5, fontWeight: 600, color: tokens.brand, fontFamily: tokens.fontFamily, textAlign: 'left', display: 'block' }}>
            View all files
          </Typography>
        </Box>
      )}

      {(() => {
        const privacy = [
          config.chat_feature.is_block_user && !isGroup ? 'Block contact' : '',
          config.chat_feature.is_delete_conversation && isGroup ? 'Delete chat' : '',
          config.chat_feature.is_delete_conversation ? (isGroup ? 'Delete and exit' : 'Delete conversation') : '',
        ].filter(Boolean);
        if (privacy.length === 0) return null;
        return (
          <>
            {!isGroup && config.chat_feature.is_media_file_sharing && <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />}
            <Typography sx={{ fontSize: tokens.fontSize - 0.5, fontWeight: 700, fontFamily: tokens.fontFamily, mb: 1, textAlign: 'left' }}>Privacy</Typography>
            <Stack spacing={0.9} sx={{ mb: 1.6 }}>
              {privacy.map((label) => (
                <Stack key={label} direction="row" spacing={0.9} alignItems="center" onClick={() => onOpenModal('confirm')} sx={{ cursor: 'pointer', color: '#e5484d' }}>
                  {label === 'Block contact' ? <BlockOutlinedIcon sx={{ fontSize: 18 }} /> : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                  <Typography sx={{ fontSize: tokens.fontSize - 1, fontWeight: 600, fontFamily: tokens.fontFamily, color: 'inherit' }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
          </>
        );
      })()}

      {!isGroup && (config.chat_feature.is_media_file_sharing || config.chat_feature.is_block_user || config.chat_feature.is_delete_conversation) && (
        <Box sx={{ borderTop: `1px solid ${tokens.border}`, mb: 1.6 }} />
      )}

      <Typography sx={{ fontSize: tokens.fontSize - 0.5, fontWeight: 700, fontFamily: tokens.fontFamily, mb: 0.6, textAlign: 'left' }}>
        About this conversation
      </Typography>
      <Typography sx={{ fontSize: tokens.fontSize - 1.5, color: tokens.secondary, fontFamily: tokens.fontFamily, lineHeight: 1.45, textAlign: 'left' }}>
        Keep messages, files, voice, and video together in a single place.
      </Typography>
    </Box>
  );
};

const BottomNav: React.FC<{
  tokens: ThemeTokens;
  items: Array<{ id: string; label: string; icon: React.ReactElement; badge?: number }>;
  activeId: string;
  onSelect?: (id: string) => void;
}> = ({ tokens, items, activeId, onSelect }) => {
  if (items.length === 0) return null;
  return (
    <Box
      sx={{
        height: 60,
        borderTop: `1px solid ${tokens.border}`,
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        backgroundColor: tokens.surface,
        flexShrink: 0,
      }}
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
            <Box
            key={item.id}
            onClick={() => onSelect?.(item.id)}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              cursor: onSelect ? 'pointer' : 'default',
              color: active ? tokens.brand : tokens.muted,
              fontSize: tokens.fontSize - 1.5,
              fontWeight: active ? 700 : 500,
            }}
          >
            <Box sx={{ position: 'relative', display: 'flex' }}>
              {item.icon}
              {!!item.badge && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -8,
                    minWidth: 16,
                    height: 16,
                    px: 0.4,
                    borderRadius: 8,
                    backgroundColor: tokens.brand,
                    color: '#fff',
                    fontSize: 9.5,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.badge}
                </Box>
              )}
            </Box>
            {item.label}
          </Box>
        );
      })}
    </Box>
  );
};

const IconRail: React.FC<{
  tokens: ThemeTokens;
  items: Array<{ id: string; label: string; icon: React.ReactElement; badge?: number }>;
  activeId: string;
  showPresence?: boolean;
  onSelect?: (id: string) => void;
}> = ({ tokens, items, activeId, showPresence = true, onSelect }) => (
  <Box
    sx={{
      width: 68,
      flexShrink: 0,
      borderRight: `1px solid ${tokens.border}`,
      backgroundColor: tokens.surface,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      py: 1.5,
      gap: 0.75,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        backgroundColor: tokens.brand,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 1.25,
        boxShadow: `0 8px 16px ${withAlpha(tokens.brand, '44')}`,
      }}
    >
      <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
    </Box>

    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.65, width: '100%', alignItems: 'center' }}>
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <Box
            key={item.id}
            onClick={() => onSelect?.(item.id)}
            sx={{
              position: 'relative',
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: onSelect ? 'pointer' : 'default',
              color: active ? tokens.brand : tokens.muted,
              backgroundColor: active ? tokens.softBrand : 'transparent',
              '&::before': active
                ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 10,
                    bottom: 10,
                    width: 3,
                    borderRadius: 4,
                    backgroundColor: tokens.brand,
                  }
                : undefined,
            }}
          >
            {item.icon}
            {!!item.badge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  minWidth: 16,
                  height: 16,
                  px: 0.35,
                  borderRadius: 8,
                  backgroundColor: tokens.brand,
                  color: '#fff',
                  fontSize: 9.5,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.badge}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>

    <AvatarBubble
      name="Ajit"
      color={withAlpha(tokens.brand, '22')}
      size={34}
      online={showPresence}
      ink={tokens.brand}
      fontFamily={tokens.fontFamily}
    />
  </Box>
);

type LiveCallFeature = 'is_shared_screen' | 'is_invite_users' | 'is_remove_participants' | 'is_microphone_control' | 'is_make_host_call';

const LIVE_CALL_FEATURES: LiveCallFeature[] = [
  'is_shared_screen',
  'is_invite_users',
  'is_remove_participants',
  'is_microphone_control',
  'is_make_host_call',
];

const CrownIcon = ({ size = 16 }: { size?: number }) => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: size, height: size, display: 'block' }}>
    <path fill="currentColor" d="M4 8.2 8.2 12 12 6.4 15.8 12 20 8.2V17a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8.2Zm1.2 8.3h13.6v-5.2l-2.6 2.3L12 8.9 7.8 13.6 5.2 11.3v5.2Z" />
  </Box>
);

const Portrait: React.FC<{ shirt: string; size?: number; glow?: string }> = ({ shirt, size = 88, glow }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: glow || '#EEF2FF',
      boxShadow: glow ? `0 0 0 ${Math.max(6, Math.round(size * 0.08))}px ${glow}` : 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
    <Box component="svg" viewBox="0 0 80 80" sx={{ width: size * 0.92, height: size * 0.92 }}>
      <ellipse cx="40" cy="76" rx="28" ry="18" fill={shirt} />
      <circle cx="40" cy="36" r="15" fill="#F3C6A0" />
      <path d="M20 36c1-16 9-24 20-24s19 8 20 24c-5 4-10 6-20 6s-15-2-20-6z" fill="#1E293B" />
      <circle cx="34.5" cy="37" r="1.4" fill="#334155" />
      <circle cx="45.5" cy="37" r="1.4" fill="#334155" />
      <path d="M35 42.5c1.4 1.6 8.6 1.6 10 0" stroke="#C47B5A" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </Box>
  </Box>
);

const OngoingCallScreen: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  feature: LiveCallFeature;
  panel: 'host' | 'people' | null;
  sharing: boolean;
  onClose: () => void;
  onPanel: (panel: 'host' | 'people' | null) => void;
  onSharing: (sharing: boolean) => void;
}> = ({ tokens, config, feature, panel, sharing, onClose, onPanel, onSharing }) => {
  const call = config.call_feature;
  const hostActions = call.is_microphone_control || call.is_remove_participants || call.is_make_host_call;
  const highlight = (key: LiveCallFeature) => feature === key;
  const tileMic = (
    <Box sx={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: '8px', backgroundColor: '#fff', border: '1px solid #E5E7EB', color: '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <MicNoneIcon sx={{ fontSize: 14 }} />
    </Box>
  );
  const nameChip = (name: string, note: string) => (
    <Box sx={{ position: 'absolute', left: 10, bottom: 10, px: 0.85, py: 0.35, borderRadius: '8px', backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.08)', textAlign: 'left' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, fontFamily: tokens.fontFamily }}>{name}</Typography>
      <Typography sx={{ fontSize: 10, color: tokens.secondary, lineHeight: 1.2, fontFamily: tokens.fontFamily }}>{note}</Typography>
    </Box>
  );
  const stageCard = (children: React.ReactNode, flex = 1) => (
    <Box sx={{ position: 'relative', flex, minHeight: 0, borderRadius: '18px', backgroundColor: '#fff', border: '1px solid #E6EDF5', overflow: 'hidden', backgroundImage: 'radial-gradient(circle at 50% 42%, rgba(191,219,254,0.7), rgba(255,255,255,0) 58%)' }}>
      {children}
    </Box>
  );
  const barButton = (icon: React.ReactNode, active: boolean, onClick?: () => void, danger = false) => (
    <Box
      onClick={onClick}
      sx={{
        width: 36,
        height: 36,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        backgroundColor: danger ? '#F0434B' : active ? withAlpha(tokens.brand, '14') : '#F4F6F8',
        color: danger ? '#fff' : active ? tokens.brand : '#64748B',
        border: `1px solid ${danger ? 'transparent' : active ? tokens.brand : 'transparent'}`,
        boxShadow: active ? `0 0 0 3px ${withAlpha(tokens.brand, '18')}` : 'none',
      }}
    >
      {icon}
    </Box>
  );
  const hostButton = (label: string, icon: React.ReactNode, active: boolean) => (
    <Stack
      direction="row"
      spacing={0.45}
      alignItems="center"
      sx={{
        px: 1,
        py: 0.55,
        borderRadius: '10px',
        border: `1px solid ${active ? tokens.brand : '#E5E7EB'}`,
        backgroundColor: active ? withAlpha(tokens.brand, '12') : '#fff',
        color: active ? tokens.brand : tokens.primary,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: tokens.fontFamily,
      }}
    >
      {icon}
      {label}
    </Stack>
  );

  const participants = [
    { name: 'Mira Sharma', shirt: '#7C3AED', note: 'Speaking', glow: 'rgba(196,181,253,0.55)' },
    { name: 'Arjun Mehta', shirt: '#F97316', note: 'Participant', glow: 'rgba(253,186,116,0.45)' },
  ];

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, backgroundColor: '#F5F7FB', display: 'flex', flexDirection: 'column', fontFamily: tokens.fontFamily }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1.1, backgroundColor: '#fff', borderBottom: '1px solid #EEF2F6' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 28, height: 28, borderRadius: '8px', backgroundColor: '#EFF6FF', color: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <VideocamOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.15 }}>h4</Typography>
            <Stack direction="row" spacing={0.45} alignItems="center">
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22C55E' }} />
              <Typography sx={{ fontSize: 11, color: tokens.secondary }}>Live · 3 participants</Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.8} alignItems="center">
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ px: 1, py: 0.35, borderRadius: '999px', border: '1px solid #E5E7EB', color: tokens.secondary }}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, fontFamily: tokens.fontFamily }}>00:17</Typography>
            <WifiIcon sx={{ fontSize: 15, color: '#94A3B8' }} />
          </Stack>
          <Box onClick={onClose} sx={{ width: 28, height: 28, borderRadius: '8px', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: tokens.secondary }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', p: 1.25, pb: 8.5 }}>
        {sharing && call.is_shared_screen ? (
          <Box sx={{ height: '100%', display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1.7, minWidth: 0, borderRadius: '18px', backgroundColor: '#F8FAFC', border: '1px solid #E6EDF5', p: 1.25, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.8, py: 0.3, borderRadius: '999px', backgroundColor: '#DCFCE7', color: '#15803D' }}>
                  <ScreenShareOutlinedIcon sx={{ fontSize: 13 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: tokens.fontFamily }}>You are sharing</Typography>
                </Stack>
                <MoreHorizIcon sx={{ fontSize: 16, color: tokens.secondary }} />
              </Stack>
              <Typography sx={{ mt: 1.4, fontSize: 10, letterSpacing: '0.08em', fontWeight: 700, color: '#60A5FA', textAlign: 'left' }}>CARE OVERVIEW</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 800, textAlign: 'left', lineHeight: 1.2 }}>Today&apos;s patient activity</Typography>
              <Stack direction="row" spacing={1.1} alignItems="flex-end" sx={{ flex: 1, px: 1, pt: 2 }}>
                {[42, 78, 56, 96, 70, 118, 86].map((height) => (
                  <Box key={height} sx={{ flex: 1, height: `${height}%`, maxHeight: 150, borderRadius: '8px 8px 4px 4px', backgroundColor: '#93C5FD' }} />
                ))}
              </Stack>
            </Box>
            <Stack spacing={1} sx={{ width: '32%', minWidth: 120 }}>
              {[{ name: 'You', note: 'You · Host', shirt: '#3B82F6', glow: 'rgba(191,219,254,0.7)' }, ...participants].map((person) => (
                <Box key={person.name} sx={{ position: 'relative', flex: 1, minHeight: 0, borderRadius: '16px', backgroundColor: '#fff', border: '1px solid #E6EDF5', overflow: 'hidden', backgroundImage: 'radial-gradient(circle at 50% 40%, rgba(191,219,254,0.65), rgba(255,255,255,0) 60%)' }}>
                  {tileMic}
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Portrait shirt={person.shirt} size={64} glow={person.glow} />
                  </Box>
                  {nameChip(person.name, person.note)}
                </Box>
              ))}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', gap: 1 }}>
            {stageCard(
              <>
                {tileMic}
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Portrait shirt="#3B82F6" size={108} glow="rgba(191,219,254,0.85)" />
                </Box>
                {nameChip('You', 'You · Host')}
              </>,
              1.7
            )}
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              {participants.map((person) => (
                <React.Fragment key={person.name}>
                  {stageCard(
                    <>
                      {tileMic}
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Portrait shirt={person.shirt} size={78} glow={person.glow} />
                      </Box>
                      {nameChip(person.name, person.note)}
                    </>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
        )}

        {panel === 'host' && (
          <Box sx={{ position: 'absolute', top: 10, right: 10, bottom: 74, width: 280, maxWidth: '86%', borderRadius: '16px', backgroundColor: '#fff', boxShadow: '0 16px 40px rgba(15,23,42,0.12)', border: '1px solid #EEF2F6', display: 'flex', flexDirection: 'column', overflow: 'hidden', textAlign: 'left' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 1.5, pt: 1.4 }}>
              <Stack direction="row" spacing={0.8}>
                <Box sx={{ color: tokens.brand, mt: 0.2 }}><CrownIcon size={16} /></Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>Host controls</Typography>
                  <Typography sx={{ fontSize: 11, color: tokens.secondary }}>Manage access and roles</Typography>
                </Box>
              </Stack>
              <Box onClick={() => onPanel(null)} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}><CloseIcon sx={{ fontSize: 16 }} /></Box>
            </Stack>
            <Typography sx={{ px: 1.5, pt: 1.2, pb: 1, fontSize: 12, color: tokens.secondary, lineHeight: 1.45 }}>
              Choose a participant to manage their audio, access, or role.
            </Typography>
            <Box sx={{ px: 1.25, overflowY: 'auto', ...hideScrollbarSx }}>
              {participants.map((person) => (
                <Box key={person.name} sx={{ mb: 1.4 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7 }}>
                    <Portrait shirt={person.shirt} size={32} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>{person.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: tokens.secondary }}>Participant</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    {call.is_microphone_control && hostButton('Mute', <MicOffOutlinedIcon sx={{ fontSize: 14 }} />, highlight('is_microphone_control'))}
                    {call.is_remove_participants && hostButton('Remove', <DeleteOutlineIcon sx={{ fontSize: 14 }} />, highlight('is_remove_participants'))}
                    {call.is_make_host_call && hostButton('Make host', <CrownIcon size={13} />, highlight('is_make_host_call'))}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {panel === 'people' && (
          <Box sx={{ position: 'absolute', top: 10, right: 10, bottom: 74, width: 280, maxWidth: '86%', borderRadius: '16px', backgroundColor: '#fff', boxShadow: '0 16px 40px rgba(15,23,42,0.12)', border: '1px solid #EEF2F6', display: 'flex', flexDirection: 'column', overflow: 'hidden', textAlign: 'left' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 1.5, pt: 1.4, pb: 1 }}>
              <Stack direction="row" spacing={0.8}>
                <PeopleOutlineIcon sx={{ fontSize: 18, color: tokens.brand, mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>Participants</Typography>
                  <Typography sx={{ fontSize: 11, color: tokens.secondary }}>3 people in this call</Typography>
                </Box>
              </Stack>
              <Box onClick={() => onPanel(null)} sx={{ cursor: 'pointer', color: tokens.secondary, display: 'flex' }}><CloseIcon sx={{ fontSize: 16 }} /></Box>
            </Stack>
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, ...hideScrollbarSx }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: tokens.secondary, mb: 0.8 }}>IN THIS CALL</Typography>
              {[
                { name: 'You', note: 'Host · You', shirt: '#3B82F6', muted: false },
                { name: 'Mira Sharma', note: 'Connected · Speaking', shirt: '#7C3AED', muted: false },
                { name: 'Arjun Mehta', note: 'Connected', shirt: '#F97316', muted: true },
              ].map((person) => (
                <Stack key={person.name} direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Portrait shirt={person.shirt} size={32} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>{person.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: tokens.secondary }}>{person.note}</Typography>
                  </Box>
                  {person.muted ? <MicOffOutlinedIcon sx={{ fontSize: 16, color: tokens.secondary }} /> : <MicNoneIcon sx={{ fontSize: 16, color: tokens.secondary }} />}
                </Stack>
              ))}
              {call.is_invite_users && (
                <>
                  <Typography sx={{ mt: 1.2, mb: 0.8, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: tokens.secondary }}>INVITE PEOPLE</Typography>
                  {[
                    ['Support assistant', 'Customer success'],
                    ['Raghav', 'Available'],
                    ['Vivan', 'Available'],
                    ['Nisha Gupta', 'Available'],
                    ['Rahul Mehta', 'Available'],
                  ].map(([name, note]) => (
                    <Stack key={name} direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Portrait shirt="#93C5FD" size={32} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>{name}</Typography>
                        <Typography sx={{ fontSize: 11, color: tokens.secondary }}>{note}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.3} alignItems="center" sx={{ px: 0.9, py: 0.35, borderRadius: '999px', border: `1px solid ${highlight('is_invite_users') ? tokens.brand : '#E5E7EB'}`, color: highlight('is_invite_users') ? tokens.brand : tokens.primary, backgroundColor: highlight('is_invite_users') ? withAlpha(tokens.brand, '10') : '#fff' }}>
                        <PersonAddAltOutlinedIcon sx={{ fontSize: 13 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: tokens.fontFamily, color: 'inherit' }}>Add</Typography>
                      </Stack>
                    </Stack>
                  ))}
                </>
              )}
            </Box>
          </Box>
        )}

        <Stack
          direction="row"
          spacing={0.7}
          alignItems="center"
          sx={{ position: 'absolute', left: '50%', bottom: 14, transform: 'translateX(-50%)', px: 1, py: 0.7, borderRadius: '16px', backgroundColor: '#fff', boxShadow: '0 10px 30px rgba(15,23,42,0.12)', border: '1px solid #EEF2F6' }}
        >
          {barButton(<MicOffOutlinedIcon sx={{ fontSize: 18 }} />, false)}
          {call.is_shared_screen && barButton(<ScreenShareOutlinedIcon sx={{ fontSize: 18 }} />, highlight('is_shared_screen') || sharing, () => onSharing(!sharing))}
          {barButton(<CallEndIcon sx={{ fontSize: 18 }} />, false, onClose, true)}
          {barButton(<PeopleOutlineIcon sx={{ fontSize: 18 }} />, panel === 'people' || highlight('is_invite_users'), () => onPanel(panel === 'people' ? null : 'people'))}
          {hostActions && barButton(<CrownIcon size={16} />, panel === 'host' || highlight('is_microphone_control') || highlight('is_remove_participants') || highlight('is_make_host_call'), () => onPanel(panel === 'host' ? null : 'host'))}
        </Stack>
      </Box>
    </Box>
  );
};

const anyCallEnabled = (config: WidgetBuilderConfig) =>
  config.call_feature.is_one_to_one_voice_calls ||
  config.call_feature.is_one_to_one_video_calls ||
  config.call_feature.is_group_voice_calls ||
  config.call_feature.is_group_video_calls;

const DEDICATED_SCREENS: KitScreenId[] = [
  'peer-detail',
  'group-detail',
  'people',
  'calls',
  'groups',
  'scheduled',
  'schedule-form',
  'modals',
];

const ChatCallPreview: React.FC<Props> = ({ config, device }) => {
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const tokens = useMemo(() => buildTokens(config, systemDark), [config, systemDark]);
  const [activeConversationId, setActiveConversationId] = useState('mira');
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const [navId, setNavId] = useState('chats');
  const [kitView, setKitView] = useState<KitScreenId>('inbox');
  const [kitModal, setKitModal] = useState<KitModalId | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [liveCall, setLiveCall] = useState<{ feature: LiveCallFeature; panel: 'host' | 'people' | null; sharing: boolean } | null>(null);
  const callSnapshot = useRef(config.call_feature);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const font = config.typography.font?.trim();
    if (!font || typeof document === 'undefined') return;
    const id = `sampark-preview-font-${font.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [config.typography.font]);

  useEffect(() => {
    const previous = callSnapshot.current;
    const next = config.call_feature;
    callSnapshot.current = next;
    const changed = LIVE_CALL_FEATURES.find((key) => previous[key] !== next[key]);
    if (!changed) return;
    const hostFeature = changed === 'is_microphone_control' || changed === 'is_remove_participants' || changed === 'is_make_host_call';
    setLiveCall({
      feature: changed,
      panel: changed === 'is_invite_users' ? 'people' : hostFeature ? 'host' : null,
      sharing: changed === 'is_shared_screen' && next.is_shared_screen,
    });
  }, [config.call_feature]);

  useEffect(() => {
    if (!isKitScreenVisible(kitView, config)) {
      setKitView('inbox');
      setNavId('chats');
    }
    if (kitModal && !isKitModalVisible(kitModal, config)) setKitModal(null);
  }, [config, kitView, kitModal]);

  const openScreen = (id: KitScreenId, targetId?: string) => {
    setKitView(id);
    setKitModal(null);
    if (targetId && CONVERSATIONS.some((item) => item.id === targetId)) {
      setActiveConversationId(targetId);
    } else if (id === 'peer' || id === 'peer-detail') {
      setActiveConversationId('mira');
    } else if (id === 'group' || id === 'group-detail') {
      setActiveConversationId((current) => {
        const currentItem = CONVERSATIONS.find((item) => item.id === current);
        return currentItem?.isGroup ? current : 'launch';
      });
    }
    if (id === 'peer' || id === 'group') setMobileView('chat');
    if (id === 'inbox') setMobileView('list');
    if (id === 'calls') setNavId('calls');
    else if (id === 'people') setNavId('people');
    else if (id === 'groups' || id === 'group' || id === 'group-detail') setNavId('groups');
    else if (id === 'scheduled' || id === 'schedule-form') setNavId('scheduled');
    else if (id === 'inbox' || id === 'peer' || id === 'peer-detail') setNavId('chats');
  };

  const openNav = (id: string) => {
    if (id === 'calls') openScreen('calls');
    else if (id === 'people') openScreen('people');
    else if (id === 'groups') openScreen('groups');
    else if (id === 'scheduled') openScreen('scheduled');
    else openScreen('inbox');
  };

  const activeConversation =
    CONVERSATIONS.find((item) => item.id === activeConversationId) || CONVERSATIONS[0];
  const dedicated = DEDICATED_SCREENS.includes(kitView);
  const visibleConversations = CONVERSATIONS.filter((item) => !item.isGroup);

  const navItems = useMemo(() => {
    const items: Array<{ id: string; label: string; icon: React.ReactElement; badge?: number }> = [
      {
        id: 'chats',
        label: 'Chats',
        icon: <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />,
        badge: 5,
      },
    ];
    if (showCallsNav(config)) {
      items.push({
        id: 'calls',
        label: 'Calls',
        icon: <PhoneInTalkOutlinedIcon sx={{ fontSize: 20 }} />,
      });
    }
    if (config.tabs.users_tab) {
      items.push({
        id: 'people',
        label: 'People',
        icon: <PeopleOutlineIcon sx={{ fontSize: 20 }} />,
      });
    }
    if (config.tabs.group_tab) {
      items.push({
        id: 'groups',
        label: 'Groups',
        icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />,
      });
    }
    if (config.tabs.schedule_call_history_tab) {
      items.push({
        id: 'scheduled',
        label: 'Scheduled',
        icon: <AccessTimeOutlinedIcon sx={{ fontSize: 20 }} />,
      });
    }
    return items;
  }, [config]);

  const frameSx = {
    width: '100%',
    maxWidth: device === 'mobile' ? 390 : '100%',
    height: device === 'mobile' ? 'min(640px, 100%)' : 'min(720px, calc(100vh - 220px))',
    maxHeight: '100%',
    mx: 'auto',
    borderRadius: device === 'mobile' ? 4 : 3.5,
    overflow: 'hidden',
    border: `1px solid ${tokens.border}`,
    backgroundColor: tokens.surface,
    color: tokens.primary,
    fontFamily: tokens.fontFamily,
    fontSize: tokens.fontSize,
    boxShadow: '0 28px 60px rgba(15, 23, 42, 0.16)',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative' as const,
    '& .MuiTypography-root': {
      fontFamily: 'inherit',
    },
  };

  const selectConversation = (id: string) => {
    setActiveConversationId(id);
    if (kitView === 'inbox') {
      setMobileView('chat');
      setNavId('chats');
      return;
    }
    const item = CONVERSATIONS.find((entry) => entry.id === id);
    openScreen(item?.isGroup ? 'group' : 'peer', id);
  };

  const chatPane = (paneDevice: 'desktop' | 'mobile', onBack?: () => void) => (
    <ChatPane
      tokens={tokens}
      config={config}
      conversation={activeConversation}
      device={paneDevice}
      onBack={onBack}
      onVoice={() => anyCallEnabled(config) && setKitModal('outgoing')}
      onVideo={() => anyCallEnabled(config) && setKitModal('outgoing')}
      onSearch={() => config.chat_feature.is_search_message && setKitModal('search')}
      onInfo={() => {
        if (device === 'mobile') openScreen(activeConversation.isGroup ? 'group-detail' : 'peer-detail');
        else setDetailsOpen((open) => !open);
      }}
      onOpenModal={setKitModal}
      onThreadChange={(open) => {
        if (open) setDetailsOpen(false);
      }}
    />
  );

  const dedicatedScreen = (
    <KitMainScreen
      screen={kitView}
      tokens={tokens}
      config={config}
      compact={device === 'mobile'}
      onNavigate={openScreen}
      onOpenModal={setKitModal}
    />
  );

  const modal = kitModal ? <KitModalLayer modal={kitModal} tokens={tokens} config={config} onClose={() => setKitModal(null)} /> : null;
  const callScreen = liveCall ? (
    <OngoingCallScreen
      tokens={tokens}
      config={config}
      feature={liveCall.feature}
      panel={liveCall.panel}
      sharing={liveCall.sharing}
      onClose={() => setLiveCall(null)}
      onPanel={(panel) => setLiveCall((current) => (current ? { ...current, panel } : current))}
      onSharing={(sharing) => setLiveCall((current) => (current ? { ...current, sharing } : current))}
    />
  ) : null;

  if (device === 'mobile') {
    return (
      <Box sx={frameSx}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {dedicated ? (
            dedicatedScreen
          ) : mobileView === 'list' ? (
            <MessagesListPanel
              tokens={tokens}
              config={config}
              conversations={visibleConversations}
              activeId={activeConversationId}
              compact
              onAdd={
                config.chat_feature.is_public_group || config.chat_feature.is_private_and_protected_group
                  ? () => setKitModal('create-group')
                  : undefined
              }
              onSelect={selectConversation}
            />
          ) : (
            chatPane('mobile', () => {
              setMobileView('list');
              if (kitView === 'peer' || kitView === 'group') setKitView('inbox');
            })
          )}
        </Box>
        <BottomNav tokens={tokens} items={navItems} activeId={navId} onSelect={openNav} />
        {modal}
        {callScreen}
      </Box>
    );
  }

  return (
    <Box sx={frameSx}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {config.with_sidebar && (
          <IconRail tokens={tokens} items={navItems} activeId={navId} showPresence={config.chat_feature.is_user_presence} onSelect={openNav} />
        )}
        {dedicated ? (
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>{dedicatedScreen}</Box>
        ) : (
          <>
            <MessagesListPanel
              tokens={tokens}
              config={config}
              conversations={visibleConversations}
              activeId={activeConversationId}
              onAdd={
                config.chat_feature.is_public_group || config.chat_feature.is_private_and_protected_group
                  ? () => setKitModal('create-group')
                  : undefined
              }
              onSelect={selectConversation}
            />
            {chatPane('desktop')}
            {detailsOpen && (
              <ConversationSidePanel
                tokens={tokens}
                config={config}
                conversation={activeConversation}
                onClose={() => setDetailsOpen(false)}
                onOpenModal={setKitModal}
              />
            )}
          </>
        )}
      </Box>
      {modal}
      {callScreen}
    </Box>
  );
};

export default ChatCallPreview;

